import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import * as Yup from "yup";
import { Fn_AddEditData, Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import usePayrollParams from "../utils/usePayrollParams";
import useSession from "../utils/useSession";
import { isNotInUse, filterActiveMasters } from "../utils/masterFilters";
import Select from "react-select";
import { clearEmployeeCache } from "./PageList_EmployeeMaster";

const EmployeeSchema = Yup.object().shape({
  Name: Yup.string().required("Employee name is required"),
  F_FirmMaster: Yup.string().required("Firm is required"),
  MobileNo: Yup.string()
    .nullable()
    .test("len", "Mobile number cannot exceed 10 digits", (val) => !val || String(val).trim().length <= 10)
    .matches(/^[0-9]*$/, "Mobile number must contain digits only"),
  Email: Yup.string().nullable().email("Invalid email address"),
  UAN: Yup.string()
    .nullable()
    .matches(/^[0-9]*$/, "UAN number must contain digits only")
    .test("uan-max", "UAN number cannot exceed 12 digits", (val) => !val || String(val).trim().length <= 12),
  OldUAN: Yup.string()
    .nullable()
    .matches(/^[0-9]*$/, "Old UAN number must contain digits only")
    .test("olduan-max", "Old UAN number cannot exceed 12 digits", (val) => !val || String(val).trim().length <= 12),
  ESINo: Yup.string()
    .nullable()
    .matches(/^[0-9]*$/, "ESI IP number must contain digits only")
    .test("esi-len", "ESI IP number cannot exceed 17 digits", (val) => !val || String(val).trim().length <= 17),
});

const calculateRetirementDate = (dobString, retirementAge = 58) => {
  const age = Number(retirementAge) > 0 ? Number(retirementAge) : 58;
  if (!dobString) return "";
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return "";
  dob.setFullYear(dob.getFullYear() + age);
  const yyyy = dob.getFullYear();
  const mm = String(dob.getMonth() + 1).padStart(2, "0");
  const dd = String(dob.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const calculateAge = (dobString) => {
  if (!dobString) return "";
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? age : "";
};

const emptyDeptRow           = (effDate = "") => ({ F_DepartmentMaster: "", F_DesignationMaster: "", EffectiveDate: effDate });
const emptyShiftRow          = (effDate = "") => ({ F_ShiftMaster: "", EffectiveFrom: effDate });
const emptyJoinRow           = (joinDate = "", defaultJoinType = 1) => ({ JoinDate: joinDate, F_JoinTypeMaster: defaultJoinType, LeftDate: "", ReasonOfLeft: "" });
const emptySalaryRow         = (defaultType = 1, effDate = "") => ({ BasicSalary: "", EffectiveDate: effDate, F_BasicTypeMaster: defaultType });
const emptyNomineeRow        = () => ({ NomineeName: "", Address: "", F_RelationshipMaster: "", BirthDate: "", Amount: "", ParentName: "", AadharNo: "", Photo: "" });
const emptyRelativeRow       = () => ({ RelativeName: "", DateOfBirth: "", Address: "", F_RelationshipMaster: "", LivingWith: 0, ForESI: 0, AadharNo: "", Photo: "" });
const emptyPensionNomineeRow = () => ({ NomineeName: "", Address: "", F_RelationshipMaster: "", BirthDate: "", Amount: "", ParentName: "", AadharNo: "", NameHindi: "", AddressHindi: "", RelHindi: "", Photo: "" });
const emptyMinWagesRow       = (effDate = "") => ({ MinWages: "", EffectiveDate: effDate });
const emptyExtraPFRateRow    = (effDate = "") => ({ ExtraPFRate: "", IsManual: false, EffectiveDate: effDate });
const emptyAccidentRow       = (accDate = "") => ({ AccidentDate: accDate, Remark: "" });
const emptyAllowanceRow      = (effDate = "") => ({ F_AllowanceMaster: "", Amount: "", Calculation: "MANUAL", IsOnGross: false, IsPFApply: false, IsESIApply: false, IsRoundOffApply: true, IsWorkingDaysApply: false, IsApplicable: true, EffectiveFrom: effDate });
const emptyDeductionRow      = (effDate = "") => ({ F_DeductionMaster: "", Amount: "", Calculation: "MANUAL", IsOnGross: false, IsPFApply: false, IsESIApply: false, IsRoundOffApply: true, IsWorkingDaysApply: false, IsApplicable: true, EffectiveFrom: effDate });
const emptyLeaveOpeningRow   = (leaveId = "", leaveName = "", fromM = "01", fromD = "01", toM = "12", toD = "31", year = "") => ({
  F_LeaveMaster: leaveId,
  LeaveName: leaveName,
  OpeningBalance: "",
  CalenderFromMonth: fromM,
  CalenderFromDay: fromD,
  CalenderToMonth: toM,
  CalenderToDay: toD,
  OpBalYear: year || (new Date().getFullYear() - 1)
});

const formatCalendarMonthRange = (fromDay, fromMonth, toDay, toMonth) => {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fM = monthNames[parseInt(fromMonth, 10) - 1] || (fromMonth || "Jan");
  const tM = monthNames[parseInt(toMonth, 10) - 1] || (toMonth || "Dec");
  const fD = fromDay ? String(fromDay).padStart(2, "0") : "01";
  const tD = toDay ? String(toDay).padStart(2, "0") : "31";
  return `${fD} ${fM} – ${tD} ${tM}`;
};

const AddEdit_EmployeeMaster = () => {
  const [activeTab, setActiveTab] = useState("basicInfo");
  const [leaveOpeningRows, setLeaveOpeningRows] = useState([]);
  const [maxWorkers, setMaxWorkers] = useState(null);
  const [existingCount, setExistingCount] = useState(0);
  const [retirementAge, setRetirementAge] = useState(58);
  const [isAutoEmpCode, setIsAutoEmpCode] = useState(false);
  const [firmShiftTimeMappings, setFirmShiftTimeMappings] = useState([]);
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [checkDuplicatePF, setCheckDuplicatePF] = useState(false);
  const [checkDuplicateESI, setCheckDuplicateESI] = useState(false);
  const [firmEmployees, setFirmEmployees] = useState([]);

  const { firm, firmId, branch, branchId } = useSession();
  const [currentFirmId, setCurrentFirmId] = useState(firmId || "");

  // Multi ESI / Multi PF payroll parameters
  const { branchList } = usePayrollParams(currentFirmId || firmId);

  // JSON table rows
  const [deptRows,           setDeptRows]           = useState([emptyDeptRow()]);
  const [shiftRows,          setShiftRows]          = useState([emptyShiftRow()]);
  const [joinRows,           setJoinRows]           = useState([emptyJoinRow()]);
  const [salaryRows,         setSalaryRows]         = useState([emptySalaryRow(1)]);
  const [nomineeRows,        setNomineeRows]        = useState([emptyNomineeRow()]);
  const [relativeRows,       setRelativeRows]       = useState([emptyRelativeRow()]);
  const [pensionNomineeRows, setPensionNomineeRows] = useState([emptyPensionNomineeRow()]);
  const [minWagesRows,       setMinWagesRows]       = useState([emptyMinWagesRow()]);
  const [extraPFRateRows,    setExtraPFRateRows]    = useState([emptyExtraPFRateRow()]);
  const [accidentRows,       setAccidentRows]       = useState([]);
  const [allowanceRows,      setAllowanceRows]      = useState([emptyAllowanceRow()]);
  const [originalAllowanceRows, setOriginalAllowanceRows] = useState([]);
  const [allowanceViewIndex, setAllowanceViewIndex] = useState({});
  const [expandedAllowances, setExpandedAllowances] = useState({});
  // Deduction states
  const [deductionRows,      setDeductionRows]      = useState([emptyDeductionRow()]);
  const [originalDeductionRows, setOriginalDeductionRows] = useState([]);
  const [deductionViewIndex, setDeductionViewIndex] = useState({});
  const [expandedDeductions, setExpandedDeductions] = useState({});

  const [state, setState] = useState({
    id: 0,
    formData: {
      F_FirmMaster: firmId ? String(firmId) : "",
      F_BranchMaster: branchId ? String(branchId) : "",
      SerialNo: "",
      Name: "",
      FatherName: "",
      EmployeeType: "Ordinary",
      EmployeeCode: "",
      CardNo: "",
      Sex: "M",
      DateOfBirth: "",
      DateOfRetire: "",
      IsDeferredPension: false,
      PensionAge: "",
      BankPayment: true,
      F_ShiftMaster: "",
      PermanentAddress: "",
      PresentAddress: "",
      MobileNo: "",
      Email: "",
      EdQualification: "",
      EducationQualification: "",
      IsHandicap: false,
      HandicapCategory: "",
      Status: "Working",
      WorkingStatus: "Working",
      AadharNumber: "",
      AadharNo: "",
      NameInAadhar: "",
      MaritalStatus: false,
      Married: "Unmarried",
      RefundFileDate: "",
      Refunded: false,
      GratuityPaidDate: "",
      F_DepartmentMaster: "",
      F_DesignationMaster: "",
      DepartmentName: "",
      DesignationName: "",
      Remark: "",
      // ESI
      ESIApplicable: false, ESINo: "", ESIJoiningDate: "", Accident: false,
      // PF
      PFApplicable: false, EmplrPFOverLimit: false, EmpleeDedOverLimit: false,
      PensionApplicable: true, PensionOverLimit: false, HigherPension: false,
      PFNo: "", UAN: "", PFJoinDate: "", ExtraPF: "", PFCalculationType: "Percentage",
      OldMemberPF: false, OldMemberPension: false, PrevPFNo: "", OldUAN: "", DateOfExitPr: "",
      PMRPY: false, PMGKY: false, ABRY: false, FixedPFSalary: "",
      // Weekly Holiday & 26 Days Working
      WeeklyHoliday: "Sunday",
      Is26DaysWorking: false,
      // Bank
      BankName: "", BankBranch: "", IFSCCode: "", BankAccountNo: "", NameAsBank: "",
      // Documents
      PanCardNo: "", NameAsPerPan: "",
      DrivingLicenceNo: "", DLExpiryDate: "", NameAsPerDL: "",
      PassportNo: "", PassportFrom: "", PassportTill: "", PassportName: "",
      IsInternationalEmployee: false, CountryOrigin: "", Nationality: "",
      F_CountryMaster: 1,
      // KYC Fields
      VoterIDNo: "", NameAsPerVoterID: "",
      RationCardNo: "", NameAsPerRationCard: "",
      NationalPopulationNo: "", NameAsPerNPR: "",
      KYCEmployeeType: "",
      F_KYCEmployeeTypeMaster: "",
      OpeningBalanceSalary: "",
      // Pension Nominee & Opening Balance Fields
      EmployeePartOpBal: "", EmployerPartOpBal: "", OpBalYear: "", Year: "",
      LeaveBalance: "",
      Photo: "",
      PhotoFile: null,
      PhotoPreview: "",
      Signature: "",
      SignatureFile: null,
      SignaturePreview: "",
      photoSignatureJson: "",
    },
    FirmMaster:        [],
    BranchMaster:      [],
    DepartmentMaster:  [],
    DesignationMaster: [],
    ShiftMaster:       [],
    EmployeeTypeMaster:[],
    JoinTypeMaster:    [],
    ReasonOfLeftMaster:[],
    BasicTypeMaster:   [],
    RelationshipMaster:[],
    AllowanceMaster:   [],
    DeductionMaster:   [],
    LeaveMaster:       [],
    CountryMaster:     [],
    isProgress: false,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL_SAVE = "EmployeeMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic`;

  // Helper to filter shifts based on selected Firm & Branch
  const getBranchShifts = (selectedBranchId, selectedFirmId) => {
    const fId = selectedFirmId || firmId;
    const bId = selectedBranchId || branchId;

    if (firmShiftTimeMappings && firmShiftTimeMappings.length > 0) {
      let filtered = firmShiftTimeMappings;

      if (bId) {
        filtered = filtered.filter(m => String(m.F_BranchMaster) === String(bId));
      } else if (fId) {
        filtered = filtered.filter(m => String(m.F_FirmMaster) === String(fId));
      }

      if (filtered.length > 0) {
        return filtered.map(m => {
          const shiftId = m.F_ShiftMaster || m.Id;
          const matchInShiftMaster = (state.ShiftMaster || []).find(s => String(s.Id) === String(shiftId));
          const timeText = (m.InTime && m.OutTime) ? `${m.InTime} - ${m.OutTime}` : "";
          const displayName = m.Name || matchInShiftMaster?.Name;
          const name = displayName
            ? (timeText && !displayName.includes(m.InTime) ? `${displayName} (${timeText})` : displayName)
            : (timeText ? `Shift (${timeText})` : `Shift #${shiftId}`);

          return {
            Id: shiftId,
            Name: name,
            InTime: m.InTime,
            OutTime: m.OutTime,
            F_BranchMaster: m.F_BranchMaster,
            F_FirmMaster: m.F_FirmMaster,
            IsNotInUse: m.IsNotInUse || matchInShiftMaster?.IsNotInUse
          };
        });
      }
    }

    return state.ShiftMaster || [];
  };

  const checkMaxWorkers = (fId, recordId = 0) => {
    if (!fId) {
      setMaxWorkers(null);
      setExistingCount(0);
      setRetirementAge(58);
      setIsAutoEmpCode(false);
      setCheckDuplicatePF(false);
      setCheckDuplicateESI(false);
      setFirmEmployees([]);
      return;
    }
    
    Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${fId}`)
      .then(resParams => {
        const param = resParams?.[0];
        const age = param?.RetirementAge;
        setRetirementAge(age !== undefined && age !== null && Number(age) > 0 ? Number(age) : 58);
        
        const autoGen = !!param?.IsAuto;
        setIsAutoEmpCode(autoGen);

        setCheckDuplicatePF(!!param?.IsCheckDuplicatePF);
        setCheckDuplicateESI(!!param?.IsCheckDuplicateESI);

        Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/GetMaxWorkersByFirmId%20/Id/${fId}`)
          .then(res => {
            const max = res?.[0]?.MaxWorkers;
            const maxNum = max !== undefined && max !== null ? Number(max) : null;
            setMaxWorkers(maxNum);
            
            Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataByFirm/Id/${fId}`)
              .then(resEmps => {
                const count = resEmps ? resEmps.length : 0;
                setExistingCount(count);
                setFirmEmployees(Array.isArray(resEmps) ? resEmps : []);
                if (recordId === 0 && maxNum !== null && maxNum > 0 && count >= maxNum) {
                  alert(`Warning: Firm's limit of ${maxNum} workers has been reached. You will not be able to save this employee.`);
                }
              })
              .catch(() => {
                setExistingCount(0);
                setFirmEmployees([]);
              });
          })
          .catch(() => setMaxWorkers(null));
      })
      .catch(() => {
        setRetirementAge(58);
        setIsAutoEmpCode(false);
        setCheckDuplicatePF(false);
        setCheckDuplicateESI(false);
        setFirmEmployees([]);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      Fn_FillListData(dispatch, setState, "FirmMaster",        `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);
      if (firmId) {
        Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`);
      }
      Fn_FillListData(dispatch, setState, "DepartmentMaster",  `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "DesignationMaster", `${API_WEB_URLS.MASTER}/0/token/DesignationMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "ShiftMaster",       `${API_WEB_URLS.MASTER}/0/token/ShiftMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "EmployeeTypeMaster", `${API_WEB_URLS.MASTER}/0/token/EmployeeTypeMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "JoinTypeMaster",    `${API_WEB_URLS.MASTER}/0/token/JoinTypeMaster/Id/0`)
        .then(resJoinTypes => {
          if (resJoinTypes && resJoinTypes.length > 0) {
            const joinType = resJoinTypes.find(j => String(j.Name).toLowerCase().trim() === "join" || j.Id === 1 || String(j.Name).toLowerCase().includes("join"));
            const defaultJoinId = joinType ? joinType.Id : resJoinTypes[0].Id;
            setJoinRows(prev => prev.map((r, i) => (i === 0 && (!r.F_JoinTypeMaster || r.F_JoinTypeMaster === "") ? { ...r, F_JoinTypeMaster: defaultJoinId } : r)));
          }
        });
      Fn_FillListData(dispatch, setState, "ReasonOfLeftMaster", `${API_WEB_URLS.MASTER}/0/token/ReasonOfLeftMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "BasicTypeMaster",   `${API_WEB_URLS.MASTER}/0/token/BasicTypeMaster/Id/0`)
        .then(resTypes => {
          if (resTypes && resTypes.length > 0) {
            const monthly = resTypes.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
            const defaultId = monthly ? monthly.Id : resTypes[0].Id;
            setSalaryRows(prev => prev.map(r => (!r.F_BasicTypeMaster ? { ...r, F_BasicTypeMaster: defaultId } : r)));
          }
        });
      Fn_FillListData(dispatch, setState, "RelationshipMaster",`${API_WEB_URLS.MASTER}/0/token/RelationshipMaster/Id/0`);
      Fn_FillListData(dispatch, setState, "AllowanceMaster",   `${API_WEB_URLS.MASTER}/0/token/AllowanceMasterForEmployeeEntry/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "DeductionMaster",   `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "LeaveMaster",       `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${firmId || 0}`);
      Fn_FillListData(dispatch, setState, "CountryMaster",     `${API_WEB_URLS.MASTER}/0/token/CountryMaster/Id/0`);

      // Fetch Firm Shift Time Mappings for branch filtering
      try {
        const mappingRes = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/FirmShiftTimeData/Id/0`);
        let parsedMappings = [];
        if (mappingRes && Array.isArray(mappingRes) && mappingRes.length > 0) {
          const jsonStr = mappingRes[0]?.FirmShiftTimeJson;
          if (jsonStr) {
            try {
              parsedMappings = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
            } catch (err) {
              console.error("Error parsing FirmShiftTimeJson:", err);
              parsedMappings = [];
            }
          } else if (Array.isArray(mappingRes[0])) {
            parsedMappings = mappingRes[0];
          } else if (mappingRes[0]?.F_FirmMaster) {
            parsedMappings = mappingRes;
          }
        }
        setFirmShiftTimeMappings(parsedMappings || []);
      } catch (err) {
        console.error("Error fetching FirmShiftTimeData:", err);
      }

      const Id = (location.state && location.state.Id) || 0;
      if (Id > 0) {
        const res = await Fn_FillListData(dispatch, setState, "Data", API_URL_EDIT + "/" + Id + "/" + (firmId || 0));

        if (res && res.length > 0) {
          const d = res[0];
          const tryParse = (val, def) => { try { const p = JSON.parse(val); return Array.isArray(p) && p.length ? p : [def()]; } catch(_) { return [def()]; } };
          if (d.DepartmentJson)     setDeptRows(tryParse(d.DepartmentJson,           emptyDeptRow));
          const parsedShifts = d.ShiftJson ? tryParse(d.ShiftJson, emptyShiftRow) : [emptyShiftRow()];
          if (d.F_ShiftMaster && (!parsedShifts[0]?.F_ShiftMaster || String(parsedShifts[0].F_ShiftMaster) === "0" || parsedShifts[0].F_ShiftMaster === "")) {
            parsedShifts[0] = { ...parsedShifts[0], F_ShiftMaster: String(d.F_ShiftMaster) };
          }
          setShiftRows(parsedShifts);
          if (d.JoinJson)           setJoinRows(tryParse(d.JoinJson,                 emptyJoinRow));
          if (d.SalaryJson)         setSalaryRows(tryParse(d.SalaryJson,             emptySalaryRow));
          if (d.NomineeJson)        setNomineeRows(tryParse(d.NomineeJson,           emptyNomineeRow));
          if (d.RelativeJson)       setRelativeRows(tryParse(d.RelativeJson,         emptyRelativeRow));
          if (d.PensionNomineeJson) setPensionNomineeRows(tryParse(d.PensionNomineeJson, emptyPensionNomineeRow));
          if (d.MinWagesJson)       setMinWagesRows(tryParse(d.MinWagesJson,         emptyMinWagesRow));
          if (d.ExtraPFRateJson)    setExtraPFRateRows(tryParse(d.ExtraPFRateJson,   emptyExtraPFRateRow));
          if (d.AccidentJson) {
            setAccidentRows(tryParse(d.AccidentJson, emptyAccidentRow));
          } else if (d.Accident && d.AccidentDate) {
            setAccidentRows([{ AccidentDate: d.AccidentDate.split("T")[0], Remark: "" }]);
          } else if (d.Accident) {
            setAccidentRows([emptyAccidentRow()]);
          } else {
            setAccidentRows([]);
          }
          if (d.AllowanceJson) {
            const parsed = tryParse(d.AllowanceJson, emptyAllowanceRow);
            setAllowanceRows(parsed);
            setOriginalAllowanceRows(JSON.parse(JSON.stringify(parsed)));
          }
          if (d.DeductionJson) {
            const parsed = tryParse(d.DeductionJson, emptyDeductionRow);
            setDeductionRows(parsed);
            setOriginalDeductionRows(JSON.parse(JSON.stringify(parsed)));
          }
          if (d.LeaveOpeningJson) {
            try {
              const parsed = typeof d.LeaveOpeningJson === "string" ? JSON.parse(d.LeaveOpeningJson) : d.LeaveOpeningJson;
              if (Array.isArray(parsed) && parsed.length > 0) {
                setLeaveOpeningRows(parsed);
              }
            } catch (err) {
              console.error("Error parsing LeaveOpeningJson:", err);
            }
          }

          let loadedPhoto = d.Photo || d.EmployeePhoto || "";
          let loadedSignature = d.Signature || "";
          const rawPhotoSigJson = d.photoSignatureJson || d.PhotoSignatureJson || "";

          if (rawPhotoSigJson) {
            try {
              const parsed = typeof rawPhotoSigJson === "string" ? JSON.parse(rawPhotoSigJson) : rawPhotoSigJson;
              const item = Array.isArray(parsed) ? parsed[0] : parsed;
              if (item) {
                if (!loadedPhoto && item.Photo) loadedPhoto = item.Photo;
                if (!loadedSignature && item.Signature) loadedSignature = item.Signature;
              }
            } catch (err) {
              console.error("Error parsing PhotoSignatureJson:", err);
            }
          }

          const activeFirm = d.F_FirmMaster || firmId;
          if (activeFirm) {
            checkMaxWorkers(activeFirm, Id);
            if (!firmId || Number(activeFirm) !== Number(firmId)) {
              Fn_FillListData(dispatch, setState, "AllowanceMaster", `${API_WEB_URLS.MASTER}/0/token/AllowanceMasterForEmployeeEntry/TBL.F_FirmMaster/${activeFirm}`);
              Fn_FillListData(dispatch, setState, "DeductionMaster", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/TBL.F_FirmMaster/${activeFirm}`);
              Fn_FillListData(dispatch, setState, "LeaveMaster",     `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${activeFirm}`);
            }
          }

          let initialESIDate = d.ESIJoiningDate ? d.ESIJoiningDate.substring(0, 10) : "";
          let initialPFDate = d.PFJoinDate ? d.PFJoinDate.substring(0, 10) : "";
          if ((!initialESIDate || !initialPFDate) && d.DepartmentJson) {
            try {
              const parsedDept = typeof d.DepartmentJson === "string" ? JSON.parse(d.DepartmentJson) : d.DepartmentJson;
              if (Array.isArray(parsedDept) && parsedDept[0]?.EffectiveDate) {
                const effDate = parsedDept[0].EffectiveDate.substring(0, 10);
                if (!initialESIDate) initialESIDate = effDate;
                if (!initialPFDate) initialPFDate = effDate;
              }
            } catch (_) {}
          }

          if (d.F_FirmMaster || firmId) {
            setCurrentFirmId(d.F_FirmMaster || firmId);
          }

          setState((prev) => ({
            ...prev,
            id: Id,
            formData: {
              F_FirmMaster:        d.F_FirmMaster || firmId || "",
              F_BranchMaster:      d.F_BranchMaster || branchId || "",
              SerialNo:            d.SerialNo || "",
              Name:                d.Name || "",
              FatherName:          d.FatherName || "",
              EmployeeType:        d.EmployeeType || "Ordinary",
              EmployeeCode:        d.EmployeeCode || d.CardNo || "",
              CardNo:              d.EmployeeCode || d.CardNo || "",
              Sex:                 d.Sex || "M",
              DateOfBirth:         d.DateOfBirth ? d.DateOfBirth.substring(0, 10) : "",
              DateOfRetire:        d.DateOfRetire ? d.DateOfRetire.substring(0, 10) : "",
              IsDeferredPension:    !!d.IsDeferredPension,
              PensionAge:          d.PensionAge || "",
              BankPayment:         d.BankPayment !== undefined && d.BankPayment !== null ? !!d.BankPayment : true,
              F_ShiftMaster:       d.F_ShiftMaster || "",
              PermanentAddress:    d.PermanentAddress || "",
              PresentAddress:      d.PresentAddress || "",
              MobileNo:            d.MobileNo || "",
              Email:               d.Email || d.EMail || "",
              EdQualification:     d.EdQualification || d.EducationQualification || "",
              EducationQualification: d.EducationQualification || d.EdQualification || "",
              IsHandicap:          !!d.IsHandicap,
              HandicapCategory:    d.HandicapCategory || "",
              Status:              d.Status || d.WorkingStatus || "Working",
              WorkingStatus:       d.Status || d.WorkingStatus || "Working",
              AadharNumber:        d.AadharNumber || d.AadharNo || "",
              AadharNo:            d.AadharNo || d.AadharNumber || "",
              NameInAadhar:        (d.AadharNumber || d.AadharNo) && String(d.AadharNumber || d.AadharNo).trim() !== "" ? (d.NameInAadhar || "") : "",
              MaritalStatus:       !!d.MaritalStatus,
              Married:             d.Married || "Unmarried",
              RefundFileDate:      d.RefundFileDate ? d.RefundFileDate.substring(0, 10) : "",
              Refunded:            !!d.Refunded,
              GratuityPaidDate:    d.GratuityPaidDate ? d.GratuityPaidDate.substring(0, 10) : "",
              F_DepartmentMaster:  d.F_DepartmentMaster || "",
              F_DesignationMaster: d.F_DesignationMaster || "",
              DepartmentName:      d.DepartmentName || "",
              DesignationName:     d.DesignationName || "",
              Remark:              d.Remark || "",
              // ESI
              ESIApplicable:       !!d.ESIApplicable,
              ESINo:               d.ESINo               || "",
              ESIJoiningDate:      initialESIDate,
              Accident:            !!d.Accident,
              // PF
              PFApplicable:        !!d.PFApplicable,
              EmplrPFOverLimit:    !!d.EmplrPFOverLimit,
              EmpleeDedOverLimit:  !!d.EmpleeDedOverLimit,
              PensionApplicable:   !!d.PensionApplicable,
              PensionOverLimit:    !!d.PensionOverLimit,
              HigherPension:       !!d.HigherPension,
              PFNo:                d.PFNo                || "",
              UAN:                 d.UAN                 ? String(d.UAN).trim().replace(/\D/g, "").slice(0, 12) : "",
              PFJoinDate:          initialPFDate,
              ExtraPF:             d.ExtraPF             || "",
              PFCalculationType:   d.PFCalculationType   || "Percentage",
              OldMemberPF:         !!d.OldMemberPF,
              OldMemberPension:    !!d.OldMemberPension,
              PrevPFNo:            d.PrevPFNo            || "",
              OldUAN:              d.OldUAN              ? String(d.OldUAN).trim().replace(/\D/g, "").slice(0, 12) : "",
              DateOfExitPr:        d.DateOfExitPr        ? d.DateOfExitPr.substring(0, 10)        : "",
              PMRPY:               !!d.PMRPY,
              PMGKY:               !!d.PMGKY,
              ABRY:                !!d.ABRY,
              FixedPFSalary:       d.FixedPFSalary       || "",
              // Other
              WeeklyHoliday:       d.WeeklyHoliday       || "Sunday",
              Is26DaysWorking:     !!(d.Is26DaysWorking),
              BankName:            d.BankName            || "",
              BankBranch:          d.BankBranch          || "",
              IFSCCode:            d.IFSCCode            || "",
              BankAccountNo:       d.BankAccountNo       || "",
              NameAsBank:          d.BankAccountNo && String(d.BankAccountNo).trim() !== "" ? (d.NameAsBank || "") : "",
              PanCardNo:           d.PanCardNo           || "",
              NameAsPerPan:        d.PanCardNo && String(d.PanCardNo).trim() !== "" ? (d.NameAsPerPan || "") : "",
              DrivingLicenceNo:    d.DrivingLicenceNo    || "",
              DLExpiryDate:        d.DLExpiryDate        ? d.DLExpiryDate.substring(0, 10)        : "",
              NameAsPerDL:         d.DrivingLicenceNo && String(d.DrivingLicenceNo).trim() !== "" ? (d.NameAsPerDL || "") : "",
              PassportNo:          d.PassportNo          || "",
              PassportFrom:        d.PassportFrom        ? d.PassportFrom.substring(0, 10)        : "",
              PassportTill:        d.PassportTill        ? d.PassportTill.substring(0, 10)        : "",
              PassportName:        d.PassportNo && String(d.PassportNo).trim() !== "" ? (d.PassportName || "") : "",
              IsInternationalEmployee: !!d.IsInternationalEmployee,
              CountryOrigin:       d.CountryOrigin       || "",
              Nationality:         d.Nationality         || "",
              F_CountryMaster:     d.F_CountryMaster     || 1,
              // KYC
              VoterIDNo:           d.VoterIDNo           || "",
              NameAsPerVoterID:    d.VoterIDNo && String(d.VoterIDNo).trim() !== "" ? (d.NameAsPerVoterID || "") : "",
              RationCardNo:        d.RationCardNo        || d.RasanCardNo || "",
              NameAsPerRationCard: (d.RationCardNo || d.RasanCardNo) && String(d.RationCardNo || d.RasanCardNo).trim() !== "" ? (d.NameAsPerRationCard || d.NameAsPerRasanCard || "") : "",
              NationalPopulationNo:d.NationalPopulationNo|| "",
              NameAsPerNPR:        d.NationalPopulationNo && String(d.NationalPopulationNo).trim() !== "" ? (d.NameAsPerNPR || "") : "",
              KYCEmployeeType:     d.KYCEmployeeType     || "",
              OpeningBalanceSalary:d.OpeningBalanceSalary|| "",
              // Pension Nominee / Op.Bal
              EmployeePartOpBal:   d.EmployeePartOpBal   || "",
              EmployerPartOpBal:   d.EmployerPartOpBal   || "",
              OpBalYear:           d.OpBalYear           || d.Year || "",
              Year:                d.Year                || d.OpBalYear || "",
              LeaveBalance:        d.LeaveBalance        || "",
              Photo:               loadedPhoto,
              PhotoFile:           null,
              PhotoPreview:        "",
              Signature:           loadedSignature,
              SignatureFile:       null,
              SignaturePreview:    "",
              photoSignatureJson:  rawPhotoSigJson,
              DepartmentJson:      d.DepartmentJson || "",
              ShiftJson:           d.ShiftJson || "",
              JoinJson:            d.JoinJson || "",
              SalaryJson:          d.SalaryJson || "",
              NomineeJson:         d.NomineeJson || "",
              RelativeJson:        d.RelativeJson || "",
              PensionNomineeJson:  d.PensionNomineeJson || "",
              MinWagesJson:        d.MinWagesJson || "",
              ExtraPFRateJson:     d.ExtraPFRateJson || "",
              AllowanceJson:       d.AllowanceJson || "",
              DeductionJson:       d.DeductionJson || "",
            },
          }));
          const isSameAddr = !!(d.PermanentAddress && d.PresentAddress && d.PermanentAddress.trim() === d.PresentAddress.trim());
          setSameAsPermanent(isSameAddr);
        }
      } else {
        setSameAsPermanent(false);
        if (firmId) {
          checkMaxWorkers(firmId, 0);
        }
        setState((prev) => ({
          ...prev,
          formData: {
            ...prev.formData,
            F_FirmMaster: firmId ? String(firmId) : "",
            F_BranchMaster: branchId ? String(branchId) : "",
          }
        }));
      }
    };
    fetchData();
  }, [dispatch, location.state, firmId, branchId]);

  // Auto-set Join Type to "Join" for new employee joining
  useEffect(() => {
    if (state.JoinTypeMaster && state.JoinTypeMaster.length > 0) {
      const joinType = state.JoinTypeMaster.find(j => String(j.Name).toLowerCase().trim() === "join" || j.Id === 1 || String(j.Name).toLowerCase().includes("join"));
      const defaultJoinId = joinType ? joinType.Id : state.JoinTypeMaster[0].Id;
      setJoinRows(prev => {
        if (prev.length > 0 && (!prev[0].F_JoinTypeMaster || prev[0].F_JoinTypeMaster === "")) {
          return prev.map((r, i) => (i === 0 && (!r.F_JoinTypeMaster || r.F_JoinTypeMaster === "") ? { ...r, F_JoinTypeMaster: defaultJoinId } : r));
        }
        return prev;
      });
    }
  }, [state.JoinTypeMaster]);

  // Load branches and firm-specific masters when firm changes
  const handleFirmChange = (selectedFId, handleChange, e) => {
    handleChange(e);
    setCurrentFirmId(selectedFId || firmId || "");
    if (selectedFId) {
      Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${selectedFId}`);
      Fn_FillListData(dispatch, setState, "DepartmentMaster", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/TBL.F_FirmMaster/${selectedFId}`);
      Fn_FillListData(dispatch, setState, "DesignationMaster", `${API_WEB_URLS.MASTER}/0/token/DesignationMaster/TBL.F_FirmMaster/${selectedFId}`);
      Fn_FillListData(dispatch, setState, "AllowanceMaster", `${API_WEB_URLS.MASTER}/0/token/AllowanceMasterForEmployeeEntry/TBL.F_FirmMaster/${selectedFId}`);
      Fn_FillListData(dispatch, setState, "DeductionMaster", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/TBL.F_FirmMaster/${selectedFId}`);
      Fn_FillListData(dispatch, setState, "LeaveMaster",     `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${selectedFId}`);
      checkMaxWorkers(selectedFId, state.id);
    } else {
      setState((prev) => ({ ...prev, BranchMaster: [], DeductionMaster: [], LeaveMaster: [] }));
      setMaxWorkers(null);
      setExistingCount(0);
      setCheckDuplicatePF(false);
      setCheckDuplicateESI(false);
      setFirmEmployees([]);
    }
  };

  // Synchronize leave opening rows with Carry Forward leaves from Leave Master
  useEffect(() => {
    if (state.LeaveMaster && state.LeaveMaster.length > 0) {
      const carryForwardLeaves = state.LeaveMaster.filter(
        l => l.IsYearCarryforward === true || l.IsYearCarryforward === 1 || String(l.IsYearCarryforward).toLowerCase() === "true"
      );
      if (carryForwardLeaves.length > 0) {
        setLeaveOpeningRows(prev => {
          const prevMap = new Map((prev || []).map(r => [String(r.F_LeaveMaster), r]));
          const merged = carryForwardLeaves.map(l => {
            const existing = prevMap.get(String(l.Id));
            if (existing) {
              return {
                ...existing,
                LeaveName: l.Name || existing.LeaveName,
                CalenderFromMonth: existing.CalenderFromMonth || l.CalenderFromMonth || "01",
                CalenderFromDay: existing.CalenderFromDay || l.CalenderFromDay || "01",
                CalenderToMonth: existing.CalenderToMonth || l.CalenderToMonth || "12",
                CalenderToDay: existing.CalenderToDay || l.CalenderToDay || "31",
              };
            }
            return emptyLeaveOpeningRow(
              l.Id,
              l.Name,
              l.CalenderFromMonth || "01",
              l.CalenderFromDay || "01",
              l.CalenderToMonth || "12",
              l.CalenderToDay || "31",
              new Date().getFullYear() - 1
            );
          });
          (prev || []).forEach(r => {
            if (r.F_LeaveMaster && !carryForwardLeaves.some(l => String(l.Id) === String(r.F_LeaveMaster))) {
              merged.push(r);
            }
          });
          return merged;
        });
      }
    }
  }, [state.LeaveMaster]);

  // Helper to validate date string YYYY-MM-DD with valid 4-digit year (>= 1900)
  const isValidDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return false;
    const clean = dateStr.split("T")[0].trim();
    const parts = clean.split("-");
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0], 10);
    return !isNaN(year) && year >= 1900 && year <= 2100;
  };

  // Helper to get current primary joining or effective date
  const getPrimaryEffectiveDate = () => {
    const joinD = joinRows?.[0]?.JoinDate ? joinRows[0].JoinDate.split("T")[0] : "";
    if (isValidDateString(joinD)) return joinD;
    const deptD = deptRows?.[0]?.EffectiveDate ? deptRows[0].EffectiveDate.split("T")[0] : "";
    if (isValidDateString(deptD)) return deptD;
    return "";
  };

  // Synchronize Joining Date across all effective date fields in the form
  const syncAllEffectiveDates = (cleanDate, setFieldValue, values, prevDate = "") => {
    if (!cleanDate) return;
    const clean = cleanDate.split("T")[0].trim();
    if (!isValidDateString(clean)) return; // Skip sync if year is incomplete/invalid (e.g. 0001 while typing)

    const shouldUpdate = (targetDate) => {
      if (!targetDate || String(targetDate).trim() === "") return true;
      const cleanTarget = String(targetDate).split("T")[0].trim();
      if (!isValidDateString(cleanTarget)) return true; // Fixes invalid year like 0001-xx-xx
      if (prevDate && isValidDateString(prevDate) && cleanTarget === prevDate.split("T")[0].trim()) return true; // Overwrite previous synced date when joining date changes
      return false;
    };

    // 1. Dept rows: row 0 EffectiveDate
    setDeptRows((prev) =>
      prev.map((r, i) => (i === 0 && shouldUpdate(r.EffectiveDate) ? { ...r, EffectiveDate: clean } : r))
    );

    // 2. Join rows: row 0 JoinDate
    setJoinRows((prev) =>
      prev.map((r, i) => {
        if (i === 0 && shouldUpdate(r.JoinDate)) {
          const defaultJoin = (state.JoinTypeMaster || []).find(j => String(j.Name).toLowerCase().trim() === "join" || j.Id === 1);
          const defaultJoinId = defaultJoin ? defaultJoin.Id : 1;
          return {
            ...r,
            JoinDate: clean,
            F_JoinTypeMaster: r.F_JoinTypeMaster ? r.F_JoinTypeMaster : defaultJoinId
          };
        }
        return r;
      })
    );

    // 3. Salary rows: row 0 or empty/invalid rows
    setSalaryRows((prev) =>
      prev.map((r, i) => (i === 0 || shouldUpdate(r.EffectiveDate) ? { ...r, EffectiveDate: clean } : r))
    );

    // 4. Shift rows: row 0 or empty/invalid rows
    setShiftRows((prev) =>
      prev.map((r, i) => (i === 0 || shouldUpdate(r.EffectiveFrom) ? { ...r, EffectiveFrom: clean } : r))
    );

    // 5. Allowance rows: empty or invalid EffectiveFrom
    setAllowanceRows((prev) =>
      prev.map((r) => (shouldUpdate(r.EffectiveFrom) ? { ...r, EffectiveFrom: clean } : r))
    );

    // 6. Deduction rows: empty or invalid EffectiveFrom
    setDeductionRows((prev) =>
      prev.map((r) => (shouldUpdate(r.EffectiveFrom) ? { ...r, EffectiveFrom: clean } : r))
    );

    // 7. Min Wages rows: row 0 or empty/invalid rows
    setMinWagesRows((prev) =>
      prev.map((r, i) => (i === 0 && shouldUpdate(r.EffectiveDate) ? { ...r, EffectiveDate: clean } : r))
    );

    // 8. Extra PF rate rows: row 0 or empty/invalid rows
    setExtraPFRateRows((prev) =>
      prev.map((r, i) => (i === 0 && shouldUpdate(r.EffectiveDate) ? { ...r, EffectiveDate: clean } : r))
    );

    // 9. Formik fields: ESIJoiningDate & PFJoinDate (only if applicable)
    if (setFieldValue) {
      if (values?.ESIApplicable) {
        if (shouldUpdate(values?.ESIJoiningDate)) {
          setFieldValue("ESIJoiningDate", clean);
        }
      }
      if (values?.PFApplicable) {
        if (shouldUpdate(values?.PFJoinDate)) {
          setFieldValue("PFJoinDate", clean);
        }
      }
    }
  };

  // Generic row helpers
  const addRow    = (setter, empty) => setter((p) => [...p, empty(getPrimaryEffectiveDate())]);
  const removeRow = (setter, index) => setter((p) => p.length === 1 ? p : p.filter((_, i) => i !== index));
  const changeRow = (setter, index, field, value) =>
    setter((p) => p.map((r, i) => i === index ? { ...r, [field]: value } : r));

  const changeJoinRow = (index, field, value, setFieldValue, values) => {
    const prevJoinDate = joinRows[index]?.JoinDate ? joinRows[index].JoinDate.split("T")[0] : "";
    setJoinRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        if (field === "JoinDate" && value && (!updated.F_JoinTypeMaster || updated.F_JoinTypeMaster === "")) {
          const defaultJoin = (state.JoinTypeMaster || []).find(j => String(j.Name).toLowerCase().trim() === "join" || j.Id === 1);
          if (defaultJoin) {
            updated.F_JoinTypeMaster = defaultJoin.Id;
          }
        }
        return updated;
      })
    );

    if (field === "JoinDate") {
      const cleanDate = value ? value.split("T")[0] : "";
      if (cleanDate && index === 0) {
        syncAllEffectiveDates(cleanDate, setFieldValue, values, prevJoinDate);
      }
    }
  };

  const changeDeptRow = (index, field, value, setFieldValue, values) => {
    const prevDeptDate = deptRows[index]?.EffectiveDate ? deptRows[index].EffectiveDate.split("T")[0] : "";
    setDeptRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

    if (field === "EffectiveDate") {
      const cleanDate = value ? value.split("T")[0] : "";
      if (cleanDate && index === 0) {
        syncAllEffectiveDates(cleanDate, setFieldValue, values, prevDeptDate);
      }
    }
  };

  const changeSalaryRow = (index, field, value) => {
    setSalaryRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        if (field === "BasicSalary") {
          const salVal = parseFloat(value) || 0;
          if (!r.F_BasicTypeMaster) {
            if (salVal > 0 && salVal <= 2500) {
              const perDayType = state.BasicTypeMaster?.find(b => b.Id === 2 || String(b.Name).toLowerCase().includes("per day") || String(b.Name).toLowerCase().includes("daily"));
              updated.F_BasicTypeMaster = perDayType ? perDayType.Id : 2;
            } else if (salVal > 2500) {
              const monthlyType = state.BasicTypeMaster?.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
              updated.F_BasicTypeMaster = monthlyType ? monthlyType.Id : 1;
            }
          }
        }
        return updated;
      })
    );
  };
  
  const changeAllowanceCalculation = (index, calculation) => {
    setAllowanceRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, Calculation: calculation };
      if (calculation === "DAILY" || calculation === "PERCENTAGE") {
        updated.IsWorkingDaysApply = false;
      }
      return updated;
    }));
  };

  const changeDeductionCalculation = (index, calculation) => {
    setDeductionRows(prev => prev.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, Calculation: calculation };
      if (calculation === "DAILY" || calculation === "PERCENTAGE") {
        updated.IsWorkingDaysApply = false;
      }
      return updated;
    }));
  };

  const ActionCell = ({ index, rows, onAdd, onRemove }) => (
    <td className="text-center align-middle" style={{ whiteSpace: "nowrap" }}>
      <button type="button" className="btn btn-success btn-xs me-1" onClick={onAdd} title="Add"><i className="fa fa-plus" /></button>
      <button type="button" className="btn btn-danger btn-xs" onClick={() => onRemove(index)} disabled={rows.length === 1} title="Remove"><i className="fa fa-minus" /></button>
    </td>
  );

  const handleSubmit = async (values) => {
    try {
      if (state.id === 0 && maxWorkers !== null && maxWorkers > 0) {
        if (existingCount >= maxWorkers) {
          alert(`Cannot create employee. Firm's limit of ${maxWorkers} workers has been reached.`);
          toast.error(`Cannot create employee. Firm's limit of ${maxWorkers} workers has been reached.`);
          return;
        }
      }

      const activeFirmId = values.F_FirmMaster || firmId || 0;
      let currentFirmEmployees = firmEmployees;
      if ((!currentFirmEmployees || currentFirmEmployees.length === 0) && activeFirmId) {
        try {
          const empList = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataByFirm/Id/${activeFirmId}`);
          if (Array.isArray(empList)) {
            currentFirmEmployees = empList;
            setFirmEmployees(empList);
          }
        } catch (_) {}
      }

      let isChkPF = checkDuplicatePF;
      let isChkESI = checkDuplicateESI;
      if (!isChkPF && !isChkESI && activeFirmId) {
        try {
          const pList = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${activeFirmId}`);
          if (pList && pList.length > 0) {
            isChkPF = !!pList[0]?.IsCheckDuplicatePF;
            isChkESI = !!pList[0]?.IsCheckDuplicateESI;
            setCheckDuplicatePF(isChkPF);
            setCheckDuplicateESI(isChkESI);
          }
        } catch (_) {}
      }

      // Check duplicate PF entry if enabled in PayrollParameter
      if (isChkPF && values.PFNo && String(values.PFNo).trim() !== "") {
        const enteredPF = String(values.PFNo).trim().toLowerCase();
        const dupPFEmp = (currentFirmEmployees || []).find(e => 
          String(e.Id) !== String(state.id) &&
          e.PFNo &&
          String(e.PFNo).trim().toLowerCase() === enteredPF
        );
        if (dupPFEmp) {
          const empDesc = `${dupPFEmp.Name || "Employee"}${dupPFEmp.EmployeeCode ? ` (${dupPFEmp.EmployeeCode})` : ""}`;
          const warnMsg = `Warning: An employee [${empDesc}] already exists with this PF number. Duplicate PF entry is not allowed.`;
          toast.warn(warnMsg);
          alert(warnMsg);
          setActiveTab("salary");
          return;
        }
      }

      // Check duplicate ESI entry if enabled in PayrollParameter
      if (isChkESI && values.ESINo && String(values.ESINo).trim() !== "") {
        const enteredESI = String(values.ESINo).trim().toLowerCase();
        const dupESIEmp = (currentFirmEmployees || []).find(e => 
          String(e.Id) !== String(state.id) &&
          e.ESINo &&
          String(e.ESINo).trim().toLowerCase() === enteredESI
        );
        if (dupESIEmp) {
          const empDesc = `${dupESIEmp.Name || "Employee"}${dupESIEmp.EmployeeCode ? ` (${dupESIEmp.EmployeeCode})` : ""}`;
          const warnMsg = `Warning: An employee [${empDesc}] already exists with this ESI number. Duplicate ESI entry is not allowed.`;
          toast.warn(warnMsg);
          alert(warnMsg);
          setActiveTab("salary");
          return;
        }
      }

      if (values.UAN) {
        const uanStr = String(values.UAN).trim();
        if (!/^\d+$/.test(uanStr)) {
          const msg = "UAN number must contain digits only.";
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
        if (uanStr.length > 12) {
          const msg = "UAN number cannot exceed 12 digits.";
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
      }

      if (values.OldUAN) {
        const oldUanStr = String(values.OldUAN).trim();
        if (!/^\d+$/.test(oldUanStr)) {
          const msg = "Old UAN number must contain digits only.";
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
        if (oldUanStr.length > 12) {
          const msg = "Old UAN number cannot exceed 12 digits.";
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
      }

      const formData = new FormData();
      const activeBranchId = values.F_BranchMaster || branchId || 0;

      const numOrZero = (v) => (v !== null && v !== undefined && v !== "" && v !== "none" && !isNaN(Number(v)) ? Number(v) : 0);
      const strVal = (v) => (v !== null && v !== undefined && String(v).trim() !== "" && v !== "none" && v !== "0" ? String(v) : "");
      const dateVal = (v) => (v !== null && v !== undefined && String(v).trim() !== "" && v !== "0" && v !== "none" ? String(v) : "");
      const boolVal = (v) => (v ? true : false);

      const empName = strVal(values.Name);
      formData.append("F_FirmMaster",        numOrZero(activeFirmId));
      formData.append("F_BranchMaster",      numOrZero(activeBranchId));
      formData.append("SerialNo",            state.id > 0 ? numOrZero(values.SerialNo) : 0);
      formData.append("Name",                empName);
      formData.append("FatherName",          strVal(values.FatherName));
      formData.append("EmployeeType",        strVal(values.EmployeeType || "Ordinary"));
      formData.append("EmployeeCode",        strVal(values.EmployeeCode || values.CardNo));
      formData.append("CardNo",              strVal(values.EmployeeCode || values.CardNo));
      formData.append("Sex",                 strVal(values.Sex || "M"));
      formData.append("DateOfBirth",         dateVal(values.DateOfBirth));
      formData.append("DateOfRetire",        dateVal(values.DateOfRetire));
      formData.append("IsDeferredPension",    boolVal(values.IsDeferredPension));
      formData.append("PensionAge",           numOrZero(values.PensionAge));
      formData.append("BankPayment",          boolVal(values.BankPayment));
      formData.append("F_ShiftMaster",        numOrZero(shiftRows[0]?.F_ShiftMaster || values.F_ShiftMaster));
      formData.append("PermanentAddress",    strVal(values.PermanentAddress));
      formData.append("PresentAddress",      strVal(values.PresentAddress));
      formData.append("MobileNo",            strVal(values.MobileNo));
      formData.append("Email",               strVal(values.Email));
      formData.append("EdQualification",      strVal(values.EdQualification || values.EducationQualification));
      formData.append("EducationQualification", strVal(values.EducationQualification || values.EdQualification));
      formData.append("IsHandicap",          boolVal(values.IsHandicap));
      formData.append("HandicapCategory",    strVal(values.HandicapCategory));
      const effectiveWorkingStatus = strVal(values.Status || values.WorkingStatus || "Working");
      formData.append("Status",              effectiveWorkingStatus);
      formData.append("WorkingStatus",       effectiveWorkingStatus);
      formData.append("AadharNumber",        strVal(values.AadharNumber || values.AadharNo));
      formData.append("AadharNo",            strVal(values.AadharNo || values.AadharNumber));
      formData.append("NameInAadhar",        (values.AadharNumber || values.AadharNo) && String(values.AadharNumber || values.AadharNo).trim() !== "" ? (strVal(values.NameInAadhar) || empName) : "");
      formData.append("MaritalStatus",       boolVal(values.MaritalStatus));
      formData.append("Married",             strVal(values.Married || "Unmarried"));
      formData.append("RefundFileDate",      dateVal(values.RefundFileDate));
      formData.append("Refunded",            boolVal(values.Refunded));
      formData.append("GratuityPaidDate",    dateVal(values.GratuityPaidDate));

      const latestDept = deptRows?.[deptRows.length - 1] || deptRows?.[0];
      const deptId = latestDept?.F_DepartmentMaster || values.F_DepartmentMaster;
      const desigId = latestDept?.F_DesignationMaster || values.F_DesignationMaster;
      formData.append("F_DepartmentMaster",  numOrZero(deptId));
      formData.append("F_DesignationMaster", numOrZero(desigId));
      formData.append("Remark",              strVal(values.Remark));
      
      // ESI
      formData.append("ESIApplicable",       boolVal(values.ESIApplicable));
      formData.append("ESINo",               strVal(values.ESINo));
      formData.append("ESIJoiningDate",      dateVal(values.ESIJoiningDate));
      formData.append("Accident",            boolVal(values.Accident));
      
      // PF
      formData.append("PFApplicable",        boolVal(values.PFApplicable));
      formData.append("EmplrPFOverLimit",    boolVal(values.EmplrPFOverLimit));
      formData.append("EmpleeDedOverLimit",  boolVal(values.EmpleeDedOverLimit));
      formData.append("PensionApplicable",   boolVal(values.PensionApplicable));
      formData.append("PensionOverLimit",    boolVal(values.PensionOverLimit));
      formData.append("HigherPension",       boolVal(values.HigherPension));
      formData.append("PFNo",                strVal(values.PFNo));
      formData.append("UAN",                 strVal(values.UAN));
      formData.append("PFJoinDate",          dateVal(values.PFJoinDate));
      formData.append("ExtraPF",             numOrZero(values.ExtraPF));
      formData.append("PFCalculationType",   strVal(values.PFCalculationType || "Percentage"));
      formData.append("OldMemberPF",         boolVal(values.OldMemberPF));
      formData.append("OldMemberPension",    boolVal(values.OldMemberPension));
      formData.append("PrevPFNo",            strVal(values.PrevPFNo));
      formData.append("OldUAN",              strVal(values.OldUAN));
      formData.append("DateOfExitPr",        dateVal(values.DateOfExitPr));
      formData.append("PMRPY",               boolVal(values.PMRPY));
      formData.append("PMGKY",               boolVal(values.PMGKY));
      formData.append("ABRY",                boolVal(values.ABRY));
      formData.append("FixedPFSalary",       numOrZero(values.FixedPFSalary));
      
      // Other
      formData.append("WeeklyHoliday",       strVal(values.WeeklyHoliday || "Sunday"));
      formData.append("Is26DaysWorking",     values.Is26DaysWorking ? true : false);
      formData.append("BankName",            strVal(values.BankName));
      formData.append("BankBranch",          strVal(values.BankBranch));
      formData.append("IFSCCode",            strVal(values.IFSCCode));
      formData.append("BankAccountNo",       strVal(values.BankAccountNo));
      formData.append("NameAsBank",          values.BankAccountNo && String(values.BankAccountNo).trim() !== "" ? (strVal(values.NameAsBank) || empName) : "");
      formData.append("PanCardNo",           strVal(values.PanCardNo));
      formData.append("NameAsPerPan",        values.PanCardNo && String(values.PanCardNo).trim() !== "" ? (strVal(values.NameAsPerPan) || empName) : "");
      formData.append("DrivingLicenceNo",    strVal(values.DrivingLicenceNo));
      formData.append("DLExpiryDate",        dateVal(values.DLExpiryDate));
      formData.append("NameAsPerDL",         values.DrivingLicenceNo && String(values.DrivingLicenceNo).trim() !== "" ? (strVal(values.NameAsPerDL) || empName) : "");
      formData.append("PassportNo",          strVal(values.PassportNo));
      formData.append("PassportFrom",        dateVal(values.PassportFrom));
      formData.append("PassportTill",        dateVal(values.PassportTill));
      formData.append("PassportName",        values.PassportNo && String(values.PassportNo).trim() !== "" ? (strVal(values.PassportName) || empName) : "");
      formData.append("IsInternationalEmployee", boolVal(values.IsInternationalEmployee));
      formData.append("CountryOrigin",       strVal(values.CountryOrigin));
      formData.append("Nationality",         strVal(values.Nationality));
      formData.append("F_CountryMaster",     numOrZero(values.F_CountryMaster));
      
      // KYC Fields
      formData.append("VoterIDNo",           strVal(values.VoterIDNo));
      formData.append("NameAsPerVoterID",    values.VoterIDNo && String(values.VoterIDNo).trim() !== "" ? (strVal(values.NameAsPerVoterID) || empName) : "");
      formData.append("RationCardNo",        strVal(values.RationCardNo));
      formData.append("NameAsPerRationCard", values.RationCardNo && String(values.RationCardNo).trim() !== "" ? (strVal(values.NameAsPerRationCard) || empName) : "");
      formData.append("NationalPopulationNo",strVal(values.NationalPopulationNo));
      formData.append("NameAsPerNPR",        values.NationalPopulationNo && String(values.NationalPopulationNo).trim() !== "" ? (strVal(values.NameAsPerNPR) || empName) : "");
      formData.append("KYCEmployeeType",     numOrZero(values.KYCEmployeeType || values.F_KYCEmployeeTypeMaster));
      formData.append("F_KYCEmployeeTypeMaster", numOrZero(values.F_KYCEmployeeTypeMaster || values.KYCEmployeeType));
      formData.append("OpeningBalanceSalary",numOrZero(values.OpeningBalanceSalary));

      // Pension Nominee & Op.Bal
      formData.append("EmployeePartOpBal",   numOrZero(values.EmployeePartOpBal));
      formData.append("EmployerPartOpBal",   numOrZero(values.EmployerPartOpBal));
      formData.append("OpBalYear",           numOrZero(values.OpBalYear || values.Year));
      formData.append("Year",                numOrZero(values.Year || values.OpBalYear));
      
      const base64ToBlob = (base64Data, defaultType = 'image/jpeg') => {
        try {
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }
          return new Blob(byteArrays, { type: defaultType });
        } catch (e) {
          return null;
        }
      };

      const toBinaryFile = (imgInput, defaultFileName = "image.jpg") => {
        if (!imgInput) return null;
        if (imgInput instanceof File || imgInput instanceof Blob) {
          return imgInput;
        }
        if (typeof imgInput === "string" && imgInput.startsWith("data:image")) {
          const parts = imgInput.split(",");
          const mimeMatch = parts[0].match(/:(.*?);/);
          const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
          const base64 = parts[1];
          const blob = base64ToBlob(base64, mimeType);
          if (blob) {
            const ext = mimeType.split("/")[1] || "jpg";
            return new File([blob], defaultFileName || `image.${ext}`, { type: mimeType });
          }
        }
        return null;
      };

      // Append Photo and Signature files in binary format
      const photoBin = toBinaryFile(values.PhotoFile || values.Photo, "employee_photo.jpg");
      if (photoBin) {
        formData.append("Photo", photoBin, "employee_photo.jpg");
        formData.append("EmployeePhoto", photoBin, "employee_photo.jpg");
      } else {
        formData.append("Photo", strVal(values.Photo));
        formData.append("EmployeePhoto", strVal(values.Photo));
      }

      const sigBin = toBinaryFile(values.SignatureFile || values.Signature, "employee_signature.jpg");
      if (sigBin) {
        formData.append("Signature", sigBin, "employee_signature.jpg");
      } else {
        formData.append("Signature", strVal(values.Signature));
      }
      
      const primaryEffectiveDate = getPrimaryEffectiveDate();

      // Sanitize JSON rows and filter empty rows
      const cleanDeptRows = deptRows.map(r => ({
        ...r,
        EffectiveDate: r.EffectiveDate || primaryEffectiveDate,
        F_DepartmentMaster: r.F_DepartmentMaster && r.F_DepartmentMaster !== "none" ? Number(r.F_DepartmentMaster) : 0,
        F_DesignationMaster: r.F_DesignationMaster && r.F_DesignationMaster !== "none" ? Number(r.F_DesignationMaster) : 0
      })).filter(r => r.F_DepartmentMaster > 0 || r.F_DesignationMaster > 0);

      const cleanShiftRows = shiftRows.map(r => ({
        ...r,
        EffectiveFrom: r.EffectiveFrom || primaryEffectiveDate,
        F_ShiftMaster: r.F_ShiftMaster && r.F_ShiftMaster !== "none" ? Number(r.F_ShiftMaster) : 0
      })).filter(r => r.F_ShiftMaster > 0);

      const defaultJoin = (state.JoinTypeMaster || []).find(j => String(j.Name).toLowerCase().trim() === "join" || j.Id === 1);
      const defaultJoinId = defaultJoin ? defaultJoin.Id : 1;
      const cleanJoinRows = joinRows.map(r => ({
        ...r,
        JoinDate: r.JoinDate || primaryEffectiveDate,
        F_JoinTypeMaster: r.F_JoinTypeMaster && r.F_JoinTypeMaster !== "none" ? Number(r.F_JoinTypeMaster) : defaultJoinId
      })).filter(r => r.F_JoinTypeMaster > 0 || r.JoinDate);

      const cleanSalaryRows = salaryRows.map(r => ({
        ...r,
        EffectiveDate: r.EffectiveDate || primaryEffectiveDate,
        BasicSalary: r.BasicSalary ? Number(r.BasicSalary) : 0,
        F_BasicTypeMaster: r.F_BasicTypeMaster && r.F_BasicTypeMaster !== "none" ? Number(r.F_BasicTypeMaster) : 0
      })).filter(r => r.BasicSalary > 0 || r.F_BasicTypeMaster > 0);

      if (values.UAN && String(values.UAN).trim().length > 12) {
        const msg = "UAN number cannot exceed 12 digits.";
        toast.error(msg);
        alert(msg);
        setActiveTab("salary");
        return;
      }
      if (values.OldUAN && String(values.OldUAN).trim().length > 12) {
        const msg = "Old UAN number cannot exceed 12 digits.";
        toast.error(msg);
        alert(msg);
        setActiveTab("salary");
        return;
      }

      if (cleanSalaryRows.length === 0) {
        const msg = "Please add at least one Salary details row with Basic Salary and Basic Type.";
        toast.error(msg);
        alert(msg);
        setActiveTab("salary");
        return;
      }

      for (let i = 0; i < cleanSalaryRows.length; i++) {
        const row = cleanSalaryRows[i];
        if (!row.F_BasicTypeMaster) {
          const msg = `Salary Row ${i + 1}: Basic Type is required.`;
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
        if (row.BasicSalary <= 0) {
          const msg = `Salary Row ${i + 1}: Basic Salary is required.`;
          toast.error(msg);
          alert(msg);
          setActiveTab("salary");
          return;
        }
      }

      // ── Clean Allowances: Automatically filter out empty or incomplete rows at save time ──
      const cleanAllowanceRows = (allowanceRows || [])
        .filter(r => {
          const masterId = r.F_AllowanceMaster && r.F_AllowanceMaster !== "none" ? Number(r.F_AllowanceMaster) : 0;
          const amt = r.Amount !== "" && r.Amount !== undefined && r.Amount !== null ? Number(r.Amount) : 0;
          return masterId > 0 && amt > 0;
        })
        .map(r => ({
          ...r,
          EffectiveFrom: r.EffectiveFrom || primaryEffectiveDate
        }));
      setAllowanceRows(cleanAllowanceRows.length > 0 ? cleanAllowanceRows : [emptyAllowanceRow()]);

      // ── Clean Deductions: Automatically filter out empty or incomplete rows at save time ──
      const cleanDeductionRows = (deductionRows || [])
        .filter(r => {
          const masterId = r.F_DeductionMaster && r.F_DeductionMaster !== "none" ? Number(r.F_DeductionMaster) : 0;
          const amt = r.Amount !== "" && r.Amount !== undefined && r.Amount !== null ? Number(r.Amount) : 0;
          return masterId > 0 && amt > 0;
        })
        .map(r => ({
          ...r,
          EffectiveFrom: r.EffectiveFrom || primaryEffectiveDate
        }));
      setDeductionRows(cleanDeductionRows.length > 0 ? cleanDeductionRows : [emptyDeductionRow()]);

      const cleanNomineeRows = nomineeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        Amount: r.Amount ? Number(r.Amount) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.NomineeName || r.F_RelationshipMaster > 0 || r.Photo);

      const cleanRelativeRows = relativeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        LivingWith: r.LivingWith ? Number(r.LivingWith) : 0,
        ForESI: r.ForESI ? Number(r.ForESI) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.RelativeName || r.F_RelationshipMaster > 0 || r.Photo);

      const cleanPensionNomineeRows = pensionNomineeRows.map(r => ({
        ...r,
        F_RelationshipMaster: r.F_RelationshipMaster && r.F_RelationshipMaster !== "none" ? Number(r.F_RelationshipMaster) : 0,
        Amount: r.Amount ? Number(r.Amount) : 0,
        Photo: r.Photo || ""
      })).filter(r => r.NomineeName || r.F_RelationshipMaster > 0 || r.NameHindi || r.Photo);

      const cleanMinWagesRows = minWagesRows.map(r => ({
        ...r,
        EffectiveDate: r.EffectiveDate || primaryEffectiveDate,
        MinWages: r.MinWages ? Number(r.MinWages) : 0
      })).filter(r => r.MinWages > 0 || r.EffectiveDate);

      const cleanExtraPFRateRows = extraPFRateRows.map(r => ({
        ...r,
        EffectiveDate: r.EffectiveDate || primaryEffectiveDate,
        ExtraPFRate: r.ExtraPFRate ? Number(r.ExtraPFRate) : 0,
        IsManual: r.IsManual ? true : false
      })).filter(r => r.ExtraPFRate > 0 || r.EffectiveDate);

      formData.append("DepartmentJson",     JSON.stringify(cleanDeptRows));
      formData.append("ShiftJson",          JSON.stringify(cleanShiftRows));
      formData.append("JoinJson",           JSON.stringify(cleanJoinRows));
      formData.append("SalaryJson",         JSON.stringify(cleanSalaryRows));
      formData.append("NomineeJson",        JSON.stringify(cleanNomineeRows));
      formData.append("RelativeJson",       JSON.stringify(cleanRelativeRows));
      formData.append("PensionNomineeJson", JSON.stringify(cleanPensionNomineeRows));
      formData.append("MinWagesJson",       JSON.stringify(cleanMinWagesRows));
      formData.append("ExtraPFRateJson",    JSON.stringify(cleanExtraPFRateRows));

      const cleanAccidentRows = (values.Accident ? accidentRows : [])
        .map(r => ({
          ...r,
          AccidentDate: r.AccidentDate ? String(r.AccidentDate).split("T")[0].trim() : "",
          Remark: r.Remark ? String(r.Remark).trim() : ""
        }))
        .filter(r => r.AccidentDate);

      formData.append("AccidentJson",       JSON.stringify(cleanAccidentRows));

      // Helper to preserve history ONLY when EffectiveFrom (Date) changes
      const buildVersionedRows = (currentRows, originalRows, masterKey) => {
        const normalizeDate = (d) => (d ? String(d).split("T")[0].trim() : "");
        
        const cleanRow = (r) => ({
          ...r,
          [masterKey]: Number(r[masterKey]),
          Amount: r.Amount ? Number(r.Amount) : 0,
          Calculation: r.Calculation || "MANUAL",
          IsOnGross: !!r.IsOnGross,
          IsPFApply: !!r.IsPFApply,
          IsESIApply: !!r.IsESIApply,
          IsRoundOffApply: r.IsRoundOffApply !== undefined ? !!r.IsRoundOffApply : true,
          IsWorkingDaysApply: !!r.IsWorkingDaysApply,
          IsApplicable: r.IsApplicable !== undefined ? !!r.IsApplicable : true,
          EffectiveFrom: normalizeDate(r.EffectiveFrom),
        });

        const validCurrent = (currentRows || [])
          .filter(r => r[masterKey] && r[masterKey] !== "none" && Number(r[masterKey]) > 0 && r.Amount !== "" && r.Amount !== undefined && r.Amount !== null && Number(r.Amount) > 0)
          .map(cleanRow);

        const validOriginal = (originalRows || [])
          .filter(r => r[masterKey] && r[masterKey] !== "none" && Number(r[masterKey]) > 0)
          .map(cleanRow);

        const resultMap = new Map();
        const makeKey = (masterId, effDate) => `${masterId}__${effDate}`;

        const origGroups = {};
        validOriginal.forEach(orig => {
          const mId = orig[masterKey];
          if (!origGroups[mId]) origGroups[mId] = [];
          origGroups[mId].push(orig);
        });

        const currentGroups = {};
        validCurrent.forEach(curr => {
          const mId = curr[masterKey];
          if (!currentGroups[mId]) currentGroups[mId] = [];
          currentGroups[mId].push(curr);
        });

        Object.keys(currentGroups).forEach(mIdStr => {
          const mId = Number(mIdStr);
          const currList = currentGroups[mId];
          const origList = origGroups[mId] || [];

          // Preserve old version in history ONLY if its EffectiveFrom date is NOT in current rows (i.e. date was changed to a new date)
          origList.forEach(orig => {
            const origDate = orig.EffectiveFrom;
            const matchingCurr = currList.find(c => c.EffectiveFrom === origDate);
            if (!matchingCurr && origDate) {
              resultMap.set(makeKey(mId, origDate), orig);
            }
          });

          // Add current rows (with either existing unchanged date or new changed date)
          currList.forEach(curr => {
            resultMap.set(makeKey(mId, curr.EffectiveFrom), curr);
          });
        });

        return Array.from(resultMap.values());
      };

      const finalAllowance = buildVersionedRows(cleanAllowanceRows, originalAllowanceRows, "F_AllowanceMaster");
      formData.append("AllowanceJson", JSON.stringify(finalAllowance));

      const finalDeduction = buildVersionedRows(cleanDeductionRows, originalDeductionRows, "F_DeductionMaster");
      formData.append("DeductionJson", JSON.stringify(finalDeduction));

      // Leave Opening Rows & Summary LeaveBalance
      const cleanLeaveOpeningRows = (leaveOpeningRows || [])
        .filter(r => r.F_LeaveMaster && r.F_LeaveMaster !== "none" && Number(r.F_LeaveMaster) > 0)
        .map(r => ({
          F_LeaveMaster: Number(r.F_LeaveMaster),
          OpeningBalance: r.OpeningBalance !== "" && r.OpeningBalance !== undefined && r.OpeningBalance !== null ? Number(r.OpeningBalance) : 0,
          CalenderFromMonth: r.CalenderFromMonth || "01",
          CalenderFromDay: r.CalenderFromDay || "01",
          CalenderToMonth: r.CalenderToMonth || "12",
          CalenderToDay: r.CalenderToDay || "31",
          OpBalYear: r.OpBalYear ? Number(r.OpBalYear) : (values.OpBalYear ? Number(values.OpBalYear) : (new Date().getFullYear() - 1))
        }));

      const totalLeaveOpening = cleanLeaveOpeningRows.reduce((sum, r) => sum + (Number(r.OpeningBalance) || 0), 0);
      const finalLeaveBalance = values.LeaveBalance !== "" && values.LeaveBalance !== undefined && values.LeaveBalance !== null
        ? Number(values.LeaveBalance)
        : totalLeaveOpening;

      formData.append("LeaveBalance", finalLeaveBalance);
      formData.append("LeaveOpeningJson", JSON.stringify(cleanLeaveOpeningRows));

      const photoVal = values.PhotoPreview || strVal(values.Photo);
      const sigVal = values.SignaturePreview || strVal(values.Signature);
      const photoSignatureData = [
        {
          Photo: photoVal,
          Signature: sigVal
        }
      ];
      const photoSignatureJsonStr = JSON.stringify(photoSignatureData);

      formData.append("photoSignatureJson", photoSignatureJsonStr);
      formData.append("PhotoSignatureJson", photoSignatureJsonStr);

      // Invalidate employee list cache so PageList_EmployeeMaster loads the fresh saved records
      try {
        clearEmployeeCache(activeFirmId || firmId);
      } catch (_) {}

      Fn_AddEditData(
        dispatch, setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE, true, "memberid", navigate, "/EmployeeMaster"
      ).catch(err => {
        console.warn("Save rejected or warning:", err);
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again.");
    }
  };

  // Global shortcut: Ctrl + S to save employee master
  useEffect(() => {
    const handleGlobalSave = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        const btn = document.getElementById("btnSubmitForm");
        if (btn && !btn.disabled) {
          btn.click();
        }
      }
    };
    window.addEventListener("keydown", handleGlobalSave);
    return () => window.removeEventListener("keydown", handleGlobalSave);
  }, []);

  // Auto-focus first editable field on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      const container = document.querySelector(".emp-tab-container") || document.querySelector(".tab-pane-content") || document.querySelector("form");
      if (container) {
        const isVisible = (el) => {
          const style = window.getComputedStyle(el);
          return style.display !== "none" && style.visibility !== "hidden" && el.offsetWidth > 0 && el.offsetHeight > 0;
        };
        const allInputs = Array.from(
          container.querySelectorAll(
            'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
          )
        ).filter(isVisible);
        if (allInputs.length > 0) {
          allInputs[0].focus();
          if (typeof allInputs[0].select === "function" && allInputs[0].type !== "checkbox" && allInputs[0].type !== "radio") {
            try { allInputs[0].select(); } catch {}
          }
        }
      }
    }, 180);
    return () => clearTimeout(timer);
  }, []);

  // Handle Next Tab navigation and auto-focus first field of new tab
  const handleNextTabClick = () => {
    const currentIndex = tabOrder.findIndex(t => t.key === activeTab);
    if (currentIndex !== -1 && currentIndex < tabOrder.length - 1) {
      const nextTabKey = tabOrder[currentIndex + 1].key;
      setActiveTab(nextTabKey);
      setTimeout(() => {
        const nextContainer = document.querySelector(".emp-tab-container") || document.querySelector(".tab-pane-content") || document.querySelector("form");
        if (nextContainer) {
          const isVisible = (el) => {
            const style = window.getComputedStyle(el);
            return style.display !== "none" && style.visibility !== "hidden" && el.offsetWidth > 0 && el.offsetHeight > 0;
          };
          const nextInputs = Array.from(
            nextContainer.querySelectorAll(
              'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])'
            )
          ).filter(isVisible);
          if (nextInputs.length > 0) {
            nextInputs[0].focus();
            if (typeof nextInputs[0].select === "function" && nextInputs[0].type !== "checkbox" && nextInputs[0].type !== "radio") {
              try { nextInputs[0].select(); } catch {}
            }
          }
        }
      }, 120);
    }
  };

  const isElementVisible = (el) => {
    if (!el || el.disabled || el.readOnly || el.type === "hidden") return false;
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
    const rect = el.getBoundingClientRect();
    return (rect.width > 0 && rect.height > 0) || (el.offsetWidth > 0 && el.offsetHeight > 0);
  };

  const handleAddNewAllowanceRow = () => {
    const newIdx = allowanceRows.length;
    addRow(setAllowanceRows, emptyAllowanceRow);
    setExpandedAllowances((prev) => ({ ...prev, [`new_${newIdx}`]: true }));
    setTimeout(() => {
      const selects = Array.from(document.querySelectorAll(".allowance-card-select")).filter(isElementVisible);
      if (selects.length > 0) {
        const lastSelect = selects[selects.length - 1];
        lastSelect.focus();
        try {
          lastSelect.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } catch {}
      }
    }, 80);
  };

  const handleAddNewDeductionRow = () => {
    const newIdx = deductionRows.length;
    addRow(setDeductionRows, emptyDeductionRow);
    setExpandedDeductions((prev) => ({ ...prev, [`new_${newIdx}`]: true }));
    setTimeout(() => {
      const selects = Array.from(document.querySelectorAll(".deduction-card-select")).filter(isElementVisible);
      if (selects.length > 0) {
        const lastSelect = selects[selects.length - 1];
        lastSelect.focus();
        try {
          lastSelect.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } catch {}
      }
    }, 80);
  };

  // Handle Enter key flow: move forward from field to field, never submit on Enter (Save is via Ctrl + S or button)
  const handleTabKeyDown = (e) => {
    // Ctrl + S (or Cmd + S) triggers Save
    if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      e.stopPropagation();
      const submitBtn = document.getElementById("btnSubmitForm");
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.click();
      }
      return;
    }

    if (e.key === "Enter") {
      const active = document.activeElement;
      if (!active) return;

      // 1. Shift + Enter in Allowances or Deductions tab → Add new row & focus its first field
      if (e.shiftKey) {
        if (activeTab === "allowances" || (active && active.closest && active.closest(".allowance-tab-pane"))) {
          e.preventDefault();
          e.stopPropagation();
          handleAddNewAllowanceRow();
          return;
        }

        if (activeTab === "deductions" || (active && active.closest && active.closest(".deduction-tab-pane"))) {
          e.preventDefault();
          e.stopPropagation();
          handleAddNewDeductionRow();
          return;
        }
      }

      // If Enter on Next Tab button → advance to next tab and focus first field
      if (active.id === "btnNextTab") {
        e.preventDefault();
        e.stopPropagation();
        handleNextTabClick();
        return;
      }

      // If Enter on Save button → let it click naturally
      if (active.id === "btnSubmitForm") {
        return;
      }

      // Allow other action buttons (Delete, Cancel, +, -) to be clicked if focused
      if (active.tagName === "BUTTON") {
        return;
      }

      // Allow multiline textarea new lines unless Ctrl is pressed
      if (active.tagName === "TEXTAREA" && !e.ctrlKey) {
        return;
      }

      // If react-select dropdown menu is open, let user select option with Enter
      const isOpenSelectMenu = document.querySelector(".select__menu, .css-26l3qy-menu, [class$='__menu']") !== null;
      if (isOpenSelectMenu) {
        return;
      }

      // CRITICAL: Prevent form submission on Enter in inputs/selects
      e.preventDefault();
      e.stopPropagation();

      const isVisible = (el) => {
        if (!el || el.disabled || el.readOnly || el.type === "hidden") return false;
        const style = window.getComputedStyle(el);
        if (style.display === "none" || style.visibility === "hidden") return false;
        const rect = el.getBoundingClientRect();
        return (rect.width > 0 && rect.height > 0) || (el.offsetWidth > 0 && el.offsetHeight > 0) || el === active;
      };

      const container =
        document.querySelector(".emp-tab-container .tab-pane-content") ||
        document.querySelector(".emp-tab-container") ||
        document.querySelector("form");
      if (!container) return;

      const allInputs = Array.from(
        container.querySelectorAll(
          'input:not([type="hidden"]):not([disabled]):not([readonly]), ' +
          'select:not([disabled]), ' +
          'textarea:not([disabled]):not([readonly])'
        )
      ).filter(isVisible);

      if (allInputs.length === 0) return;

      // Find index of current active element
      let curIndex = allInputs.indexOf(active);
      if (curIndex === -1) {
        // Try matching by containment or matching attributes
        curIndex = allInputs.findIndex((el) => el === active || el.contains(active) || active.contains(el) || (el.id && el.id === active.id) || (el.name && el.name === active.name));
      }
      if (curIndex === -1) {
        // Fallback: search by document order — find the input that comes right after active element
        for (let i = 0; i < allInputs.length; i++) {
          if (active.compareDocumentPosition(allInputs[i]) & Node.DOCUMENT_POSITION_FOLLOWING) {
            curIndex = i - 1;
            break;
          }
        }
        // If active is before all inputs, start from 0
        if (curIndex === -1 && allInputs.length > 0) {
          allInputs[0].focus();
          if (typeof allInputs[0].select === "function" && allInputs[0].type !== "checkbox" && allInputs[0].type !== "radio") {
            try { allInputs[0].select(); } catch {}
          }
          return;
        }
      }

      // If active is a radio button, jump past all other radios in the same group to the next field
      if (active.type === "radio" && active.name) {
        const nextDiff = allInputs.findIndex((el, idx) => idx > curIndex && el.name !== active.name);
        if (nextDiff !== -1) {
          curIndex = nextDiff - 1;
        }
      }

      // If active is one of PMRPY / PMGKY / ABRY and ANY of them is ticked, jump past all three to next field
      const schemeIds = ["PMRPY", "PMGKY", "ABRY"];
      if (schemeIds.includes(active.id)) {
        const isAnySchemeChecked = !!(
          active.checked ||
          document.getElementById("PMRPY")?.checked ||
          document.getElementById("PMGKY")?.checked ||
          document.getElementById("ABRY")?.checked
        );
        if (isAnySchemeChecked) {
          const nextNonScheme = allInputs.findIndex((el, idx) => idx > curIndex && !schemeIds.includes(el.id));
          if (nextNonScheme !== -1) {
            curIndex = nextNonScheme - 1;
          }
        }
      }

      if (curIndex > -1 && curIndex < allInputs.length - 1) {
        // Move to next input in current tab
        let nextEl = allInputs[curIndex + 1];

        // If next element is one of PMRPY/PMGKY/ABRY and one of them is already checked, focus that checked one
        if (nextEl && schemeIds.includes(nextEl.id)) {
          const checkedScheme = [
            document.getElementById("PMRPY"),
            document.getElementById("PMGKY"),
            document.getElementById("ABRY")
          ].find(el => el && el.checked && isVisible(el));
          if (checkedScheme) {
            nextEl = checkedScheme;
          }
        }

        // If next element is a radio button, focus the checked radio in that group if one exists
        if (nextEl && nextEl.type === "radio" && nextEl.name) {
          const checkedRadio = container.querySelector(`input[type="radio"][name="${nextEl.name}"]:checked`);
          if (checkedRadio && isVisible(checkedRadio)) {
            nextEl = checkedRadio;
          }
        }
        if (nextEl) {
          nextEl.focus();
          if (typeof nextEl.select === "function" && nextEl.type !== "checkbox" && nextEl.type !== "radio") {
            try { nextEl.select(); } catch {}
          }
        }
      } else if (curIndex >= allInputs.length - 1) {
        // At the last input of current tab: focus Next button (or Save button if final tab)
        const nextBtn = document.getElementById("btnNextTab");
        if (nextBtn && !nextBtn.disabled) {
          nextBtn.focus();
        } else {
          const saveBtn = document.getElementById("btnSubmitForm");
          if (saveBtn) saveBtn.focus();
        }
      }
    }
  };

  const standardTabs = [
    { key: "basicInfo",      label: "EMPLOYEE" },
    { key: "salary",         label: "BASIC / SALARY" },
    { key: "accidentHistory",label: "Accident History" },
    { key: "kyc",            label: "KYC" },
    { key: "pensionNominee", label: "PENSION NOMINEE" },
    { key: "department",     label: "Dept. & Join History" },
    { key: "shift",          label: "Shift History" },
    { key: "allowances",     label: "Allowances" },
    { key: "deductions",     label: "Deductions" },
  ];

  const [tabOrder, setTabOrder] = useState(() => {
    const saved = localStorage.getItem("employeeTabSequence");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const merged = [];
          parsed.forEach(p => {
            const st = standardTabs.find(s => s.key === p.key);
            if (st) merged.push(st);
          });
          standardTabs.forEach(st => {
            if (!merged.some(m => m.key === st.key)) merged.push(st);
          });
          return merged;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return standardTabs;
  });

  useEffect(() => {
    const fetchTabSequence = async () => {
      try {
        const res = await Fn_FillListData(dispatch, () => {}, "none", `${API_WEB_URLS.MASTER}/0/token/TabSequencesMaster/Id/0`);
        if (res && Array.isArray(res)) {
          const employeeTabs = res.filter(t => t.ModuleName === "EmployeeMaster");
          if (employeeTabs.length > 0) {
            employeeTabs.sort((a, b) => (a.TabId || 0) - (b.TabId || 0));
            const newOrder = [];
            employeeTabs.forEach(item => {
              const standardTab = standardTabs.find(st => st.key === item.TabName);
              if (standardTab) {
                newOrder.push(standardTab);
              }
            });
            standardTabs.forEach(st => {
              if (!newOrder.some(t => t.key === st.key)) {
                newOrder.push(st);
              }
            });

            if (newOrder.length === standardTabs.length) {
              setTabOrder(newOrder);
              localStorage.setItem("employeeTabSequence", JSON.stringify(newOrder));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching TabSequence from DB:", err);
      }
    };
    fetchTabSequence();
  }, [dispatch]);

  const saveTabSequence = (newOrder) => {
    localStorage.setItem("employeeTabSequence", JSON.stringify(newOrder));
    
    const payload = newOrder.map((t, index) => ({
      TabId: index + 1,
      TabName: t.key,
      IsActive: true
    }));

    const fd = new FormData();
    fd.append("ModuleName", "EmployeeMaster");
    fd.append("TabSequencesJson", JSON.stringify(payload));
    
    Fn_AddEditData(
      dispatch,
      () => {},
      { arguList: { id: 0, formData: fd } },
      "TabSequencesMaster/0/token",
      true,
      "memberid",
      () => {},
      "#"
    ).catch(err => console.error("Error saving TabSequence to DB:", err));
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    const sourceIndex = Number(e.dataTransfer.getData("text/plain"));
    if (sourceIndex === targetIndex) return;

    const newOrder = [...tabOrder];
    const [draggedItem] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setTabOrder(newOrder);
    saveTabSequence(newOrder);
  };

  // Reset tab container scroll to top whenever user changes tabs
  useEffect(() => {
    const container = document.querySelector(".emp-tab-container");
    if (container) {
      container.scrollTop = 0;
    }
  }, [activeTab]);

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Employee Master" />

      <style>{`
        /* Page Header Spacing */
        .page-titles {
          margin-bottom: 6px !important;
          padding: 6px 12px !important;
        }

        /* Flexible Container Space: Fit within viewport without hiding footer */
        .emp-main-wrapper {
          height: calc(100vh - 165px) !important;
          max-height: calc(100vh - 165px) !important;
          min-height: 420px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          margin-bottom: 0px !important;
        }
        .emp-compact-form {
          flex: 1 1 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100%;
        }
        /* Main Outer Page Card */
        .emp-main-card {
          flex: 1 1 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          margin-bottom: 0px !important;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
        }
        .emp-main-header {
          padding: 6px 14px !important;
          min-height: unset !important;
          flex-shrink: 0;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
          display: flex;
          align-items: center;
          border-left: 4px solid #7c69ef;
        }
        .emp-main-header .card-title {
          font-size: 14.5px !important;
          font-weight: 600;
          margin-bottom: 0 !important;
          color: #1e293b;
        }
        .emp-main-body {
          flex: 1 1 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 8px 14px 6px 14px !important;
          background: #ffffff;
        }
        .emp-compact-form form {
          flex: 1 1 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100%;
        }

        /* Inner Tab Container: Natural block flow with vertical scroll when needed */
        .emp-tab-container {
          flex: 1 1 0;
          min-height: 0;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          display: block !important;
          padding-right: 6px;
          padding-top: 4px;
          padding-bottom: 8px;
        }
        .emp-tab-container .tab-pane-content {
          display: block !important;
          width: 100% !important;
          height: auto !important;
        }

        /* Inner Cards inside tabs (Basic Salary, KYC, Deductions, Allowances, Pension) */
        .emp-compact-form .emp-tab-container .card {
          flex: none !important;
          height: auto !important;
          min-height: auto !important;
          overflow: visible !important;
          margin-bottom: 8px !important;
          border-radius: 6px !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: none !important;
          display: block !important;
          background: #ffffff;
        }
        .emp-compact-form .emp-tab-container .card-header {
          padding: 5px 10px !important;
          min-height: auto !important;
          border-left: none !important;
          border-bottom: 1px solid #e2e8f0 !important;
          background: #f8fafc !important;
          display: flex !important;
          align-items: center !important;
          flex-shrink: 0;
        }
        .emp-compact-form .emp-tab-container .card-body {
          flex: none !important;
          height: auto !important;
          min-height: auto !important;
          overflow: visible !important;
          padding: 6px 10px !important;
          background: #ffffff !important;
          display: block !important;
        }

        /* Tabs Nav - Compact & Elegant */
        .emp-compact-form .nav-tabs {
          margin-bottom: 6px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          flex-shrink: 0;
          gap: 3px;
        }
        .emp-compact-form .nav-tabs .nav-link {
          padding: 4px 10px !important;
          font-size: 11.5px !important;
          font-weight: 600 !important;
          line-height: 1.3 !important;
          border-radius: 4px 4px 0 0;
          color: #475569 !important;
          border: 1px solid #e2e8f0 !important;
          background: #f8fafc;
          transition: all 0.15s ease-in-out;
        }
        .emp-compact-form .nav-tabs .nav-link:hover {
          color: #1e293b !important;
          background: #f1f5f9;
        }
        .emp-compact-form .nav-tabs .nav-link.active {
          color: #7c69ef !important;
          background: #ffffff !important;
          border-color: #cbd5e1 #cbd5e1 #ffffff !important;
          border-bottom: 2px solid #7c69ef !important;
          box-shadow: 0 -2px 4px rgba(0,0,0,0.02);
        }

        /* ════════════════════════════════════════════════════════════════
           MATERIAL OUTLINED FLOATING NOTCH FIELD DESIGN (Exact User Image Design)
           ════════════════════════════════════════════════════════════════ */
        .emp-compact-form .emp-field-group {
          position: relative !important;
          margin-top: 6px !important;
          margin-bottom: 2px !important;
        }

        .emp-compact-form .emp-field-group > label:not(.form-check-label),
        .emp-compact-form .emp-field-group label.emp-field-label {
          position: absolute !important;
          top: -7px !important;
          left: 10px !important;
          background-color: #ffffff !important;
          padding: 0 5px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #7c69ef !important; /* Theme purple as in user image */
          line-height: 1 !important;
          z-index: 5 !important;
          margin-bottom: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          border-radius: 2px !important;
          pointer-events: none !important;
          white-space: nowrap !important;
          letter-spacing: 0.2px;
        }

        /* Outlined purple border on all inputs, selects, & control boxes */
        .emp-compact-form .emp-field-group .form-control,
        .emp-compact-form .emp-field-group .form-control-sm,
        .emp-compact-form .emp-field-group select.form-control-sm,
        .emp-compact-form .emp-field-group select,
        .emp-compact-form .emp-field-group .emp-control-box,
        .emp-compact-form .emp-field-group .emp-media-box {
          border: 1.5px solid #7c69ef !important;
          border-radius: 6px !important;
          height: 33px !important;
          min-height: 33px !important;
          padding: 3px 8px !important;
          font-size: 11.5px !important;
          background-color: #ffffff !important;
          color: #1e293b !important;
          box-shadow: none !important;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
        }

        /* Fallback for form controls outside emp-field-group (non-table) */
        .emp-compact-form input.form-control:not(table input),
        .emp-compact-form select.form-control:not(table select) {
          border: 1.5px solid #7c69ef;
          border-radius: 6px;
          height: 33px;
          min-height: 33px;
          padding: 3px 8px;
          font-size: 11.5px;
          background-color: #ffffff;
          color: #1e293b;
        }

        /* Table Inputs and Selects (Basic Salary, Nominees, Relatives, Leave, etc.) */
        .emp-compact-form table.table-sm input.form-control,
        .emp-compact-form table.table-sm select.form-control,
        .emp-compact-form table.table-sm select.form-select {
          border: 1px solid #cbd5e1 !important;
          border-radius: 4px !important;
          height: 28px !important;
          min-height: 28px !important;
          padding: 2px 6px !important;
          font-size: 11px !important;
          background-color: #ffffff !important;
          color: #1e293b !important;
          box-shadow: none !important;
        }
        .emp-compact-form table.table-sm input.form-control:focus,
        .emp-compact-form table.table-sm select.form-control:focus,
        .emp-compact-form table.table-sm select.form-select:focus {
          border-color: #7c69ef !important;
          box-shadow: 0 0 0 2px rgba(124, 105, 239, 0.2) !important;
        }

        /* Hover & Focus state */
        .emp-compact-form .emp-field-group:hover .form-control,
        .emp-compact-form .emp-field-group:hover select,
        .emp-compact-form .emp-field-group:hover .emp-control-box {
          border-color: #6350e0 !important;
        }

        .emp-compact-form .emp-field-group:focus-within .form-control,
        .emp-compact-form .emp-field-group:focus-within select,
        .emp-compact-form .emp-field-group:focus-within .emp-control-box {
          border-color: #553fd9 !important;
          outline: 0 !important;
          box-shadow: 0 0 0 2.5px rgba(124, 105, 239, 0.25) !important;
        }

        .emp-compact-form .emp-field-group:focus-within > label:not(.form-check-label),
        .emp-compact-form .emp-field-group:focus-within label.emp-field-label {
          color: #553fd9 !important;
          font-weight: 700 !important;
        }

        /* Disabled & ReadOnly styling */
        .emp-compact-form .emp-field-group .form-control:disabled,
        .emp-compact-form .emp-field-group .form-control[readonly],
        .emp-compact-form .emp-field-group select:disabled {
          background-color: #f8fafc !important;
          border-color: #94a3b8 !important;
          color: #64748b !important;
        }

        /* Checkbox Box with standard height and purple outline */
        .emp-control-box {
          height: 33px !important;
          min-height: 33px !important;
          display: flex !important;
          align-items: center !important;
          background-color: #ffffff !important;
          border: 1.5px solid #7c69ef !important;
          border-radius: 6px !important;
          padding: 0 8px !important;
          box-sizing: border-box !important;
          transition: all 0.15s ease-in-out !important;
        }
        .emp-control-box:hover {
          border-color: #6350e0 !important;
          background-color: #fcfaff !important;
        }
        .emp-control-box .form-check-input {
          cursor: pointer !important;
          margin: 0 !important;
          width: 15px !important;
          height: 15px !important;
        }
        .emp-control-box label {
          cursor: pointer !important;
          user-select: none !important;
          font-size: 10.5px !important;
          margin-bottom: 0 !important;
          margin-left: 3px !important;
          color: #334155 !important;
          font-weight: 500 !important;
          display: flex !important;
          align-items: center !important;
          white-space: nowrap !important;
        }
        .emp-control-box .form-check-inline {
          margin-right: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
        }

        /* Deferred Pension Age Pill Input */
        .emp-compact-form .emp-age-pill {
          width: 36px !important;
          height: 22px !important;
          min-height: 22px !important;
          max-height: 22px !important;
          padding: 0 4px !important;
          font-size: 11.5px !important;
          font-weight: 700 !important;
          color: #553fd9 !important;
          background-color: #f5f3ff !important;
          border: 1.5px solid #7c69ef !important;
          border-radius: 4px !important;
          text-align: center !important;
          outline: none !important;
          box-shadow: none !important;
          line-height: 20px !important;
          box-sizing: border-box !important;
          transition: all 0.15s ease-in-out !important;
        }
        .emp-compact-form .emp-age-pill:focus {
          border-color: #4338ca !important;
          background-color: #ffffff !important;
          box-shadow: 0 0 0 2px rgba(124, 105, 239, 0.25) !important;
        }

        /* Checkbox / Radio Flexbox Alignment & Fixed Dimensions */
        .emp-compact-form .form-check {
          display: inline-flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 5px !important;
          margin-bottom: 0 !important;
          min-height: 26px !important;
          height: 26px !important;
          padding-left: 0 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
        }
        .emp-compact-form .form-check-label {
          font-size: 11px !important;
          margin-bottom: 0 !important;
          color: #334155 !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          display: inline-block !important;
          white-space: nowrap !important;
          overflow: visible !important;
          text-overflow: unset !important;
        }
        .emp-compact-form input[type="checkbox"],
        .emp-compact-form input[type="radio"],
        .emp-compact-form .form-check-input {
          box-sizing: border-box !important;
          width: 14px !important;
          height: 14px !important;
          min-width: 14px !important;
          min-height: 14px !important;
          max-width: 14px !important;
          max-height: 14px !important;
          margin: 0 !important;
          padding: 0 !important;
          cursor: pointer !important;
          flex-shrink: 0 !important;
          display: inline-block !important;
          vertical-align: middle !important;
          accent-color: #6366f1 !important;
        }

        /* Tables & Action Cells */
        .emp-compact-form table.table-sm th {
          background-color: #f1f5f9 !important;
          color: #1e293b !important;
          font-weight: 600 !important;
          font-size: 11px !important;
          padding: 4px 6px !important;
          border-bottom: 1px solid #cbd5e1 !important;
        }
        .emp-compact-form table.table-sm td {
          padding: 3px 6px !important;
          font-size: 11px !important;
          vertical-align: middle !important;
        }
        .emp-compact-form .btn-xs {
          padding: 2px 6px !important;
          font-size: 11px !important;
          line-height: 1.2 !important;
          border-radius: 3px !important;
        }

        /* Anchored Action Buttons: Always pinned and visible */
        .emp-footer-actions {
          margin-top: auto !important;
          padding-top: 8px !important;
          padding-bottom: 4px !important;
          border-top: 1px solid #e2e8f0 !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          background: #ffffff !important;
          flex-shrink: 0 !important;
          position: sticky !important;
          bottom: 0 !important;
          z-index: 10 !important;
        }
      `}</style>

      <div className="row emp-main-wrapper emp-compact-form">
        <div className="col-lg-12 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
          <div className="card emp-main-card h-100 d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="card-header emp-main-header">
              <h4 className="card-title">Employee Master</h4>
            </div>
            <div className="card-body emp-main-body h-100 d-flex flex-column" style={{ minHeight: 0 }}>

              {/* Tabs Nav */}
              <ul className="nav nav-tabs mb-2 flex-wrap" role="tablist">
                {tabOrder.map((tab, idx) => (
                  <li className="nav-item" key={tab.key}>
                    <button
                      className={`nav-link${activeTab === tab.key ? " active" : ""}`}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                      style={{ cursor: "grab" }}
                    >
                      <span className="me-1 text-muted" style={{ userSelect: "none" }}>⋮⋮</span>
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              <Formik
                initialValues={state.formData}
                enableReinitialize
                validationSchema={EmployeeSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setTimeout(() => { handleSubmit(values); setSubmitting(false); }, 400);
                }}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, validateForm, setTouched }) => {
                  const currentAge = calculateAge(values.DateOfBirth);
                  const availableShifts = getBranchShifts(values.F_BranchMaster, values.F_FirmMaster);

                  return (
                    <form onSubmit={handleSubmit} onKeyDown={handleTabKeyDown} style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>

                      {/* Form Container Flexibility: Flex child taking remaining space with natural vertical scrolling */}
                      <div className="emp-tab-container" style={{ minHeight: 0, flex: "1 1 0", overflowY: "auto", overflowX: "hidden" }}>
                        {activeTab !== "basicInfo" && (
                          <div className="alert alert-info py-1 px-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-1" style={{ borderRadius: 4, fontSize: "11px", flexShrink: 0 }}>
                            <div>
                              <strong className="text-dark me-1">Emp Name:</strong>
                              <span className="text-primary fw-semibold me-3">{values.Name || "N/A"}</span>
                              
                              <strong className="text-dark me-1">Father:</strong>
                              <span className="text-muted me-3">{values.FatherName || "N/A"}</span>

                              <strong className="text-dark me-1">Emp Code:</strong>
                              <span className="text-danger fw-semibold">{values.EmployeeCode || values.CardNo || "N/A"}</span>
                            </div>
                            <div className="badge bg-light text-primary border px-2 py-0" style={{ fontSize: 10.5 }}>
                              {tabOrder.find(t => t.key === activeTab)?.label}
                            </div>
                          </div>
                        )}

                        {/* ════════════════════════════════════════════════════════════════════════
                            TAB 1: EMPLOYEE (Basic Info) - Material Outlined Notched Layout
                            ════════════════════════════════════════════════════════════════════════ */}
                        {activeTab === "basicInfo" && (
                          <div className="tab-pane-content emp-compact-tab">
                            <div className="row gx-2 gy-1">

                              {/* 1. Serial No (Conditional) */}
                              {state.id > 0 && (
                                <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                  <label className="emp-field-label">Serial No</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    name="SerialNo"
                                    value={values.SerialNo || ""}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed", fontWeight: 700 }}
                                    title={`Firm-wise Serial Number: ${values.SerialNo}`}
                                  />
                                </div>
                              )}

                              {/* 2. ESI Firm */}
                              <div className={state.id > 0 ? "col-12 col-sm-6 col-md-5 col-xl-3 emp-field-group" : "col-12 col-sm-6 col-md-6 col-xl-4 emp-field-group"}>
                                <label className="emp-field-label">
                                  ESI Firm <span className="text-danger">*</span>
                                </label>
                                <div className="position-relative w-100">
                                  {firmId ? (
                                    <>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={
                                          (state.FirmMaster || []).find((f) => String(f.Id) === String(values.F_FirmMaster || firmId))?.Name ||
                                          (typeof firm === "string" ? firm : firm?.Name) ||
                                          sessionStorage.getItem("FirmName") ||
                                          localStorage.getItem("FirmName") ||
                                          "SHREENATH JI"
                                        }
                                        readOnly
                                        disabled
                                        style={{
                                          backgroundColor: "#f8fafc",
                                          cursor: "not-allowed",
                                          fontWeight: 700,
                                          color: "#1e293b",
                                          paddingRight: "70px",
                                          border: "1.5px solid #7c69ef"
                                        }}
                                        title={`Firm is locked to: ${(state.FirmMaster || []).find((f) => String(f.Id) === String(values.F_FirmMaster || firmId))?.Name || (typeof firm === "string" ? firm : firm?.Name) || firmId}`}
                                      />
                                      <input type="hidden" name="F_FirmMaster" value={values.F_FirmMaster || firmId} />
                                      <span
                                        className="badge bg-light text-success border position-absolute"
                                        style={{
                                          right: "8px",
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          fontSize: "9.5px",
                                          padding: "2px 6px",
                                          fontWeight: 600,
                                          borderRadius: "4px",
                                          pointerEvents: "none",
                                          zIndex: 4
                                        }}
                                      >
                                        🔒 Locked
                                      </span>
                                    </>
                                  ) : (
                                    <select
                                      className="form-control form-control-sm"
                                      name="F_FirmMaster"
                                      value={values.F_FirmMaster || ""}
                                      onBlur={handleBlur}
                                      onChange={(e) => handleFirmChange(e.target.value, handleChange, e)}
                                    >
                                      <option value="">-- Select Firm --</option>
                                      {filterActiveMasters(state.FirmMaster, values.F_FirmMaster).map((f) => (
                                        <option key={f.Id} value={f.Id}>{f.Name}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                {errors.F_FirmMaster && <div className="text-danger fs-11 mt-1">{errors.F_FirmMaster}</div>}
                              </div>

                              {/* 3. Branch */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Branch</label>
                                <select
                                  className="form-control form-control-sm"
                                  name="F_BranchMaster"
                                  value={values.F_BranchMaster || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">-- Select Branch --</option>
                                  {filterActiveMasters(state.BranchMaster, values.F_BranchMaster).map((b) => <option key={b.Id} value={b.Id}>{b.Name}</option>)}
                                </select>
                              </div>

                              {/* 4. Emp Name */}
                              <div className={state.id > 0 ? "col-12 col-sm-6 col-md-6 col-xl-3 emp-field-group" : "col-12 col-sm-6 col-md-6 col-xl-3.5 emp-field-group"}>
                                <label className="emp-field-label">Emp. Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="Name"
                                  value={values.Name || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const prevName = values.Name || "";
                                    setFieldValue("Name", val);

                                    // Only auto-sync document names if document number actually has a value
                                    const docFieldConfigs = [
                                      { nameKey: "NameInAadhar", numKey: values.AadharNumber || values.AadharNo },
                                      { nameKey: "NameAsBank", numKey: values.BankAccountNo },
                                      { nameKey: "NameAsPerPan", numKey: values.PanCardNo },
                                      { nameKey: "NameAsPerDL", numKey: values.DrivingLicenceNo },
                                      { nameKey: "PassportName", numKey: values.PassportNo },
                                      { nameKey: "NameAsPerVoterID", numKey: values.VoterIDNo },
                                      { nameKey: "NameAsPerRationCard", numKey: values.RationCardNo },
                                      { nameKey: "NameAsPerNPR", numKey: values.NationalPopulationNo },
                                    ];

                                    docFieldConfigs.forEach(({ nameKey, numKey }) => {
                                      const hasNumber = numKey && String(numKey).trim() !== "";
                                      if (hasNumber) {
                                        if (!values[nameKey] || values[nameKey] === prevName) {
                                          setFieldValue(nameKey, val);
                                        }
                                      } else {
                                        setFieldValue(nameKey, "");
                                      }
                                    });
                                  }}
                                  onBlur={handleBlur}
                                  placeholder="Employee Full Name"
                                />
                                {errors.Name && <div className="text-danger fs-11 mt-1">{errors.Name}</div>}
                              </div>

                              {/* 5. Employee Type */}
                              <div className={state.id > 0 ? "col-12 col-sm-6 col-md-6 col-xl-2 emp-field-group" : "col-12 col-sm-6 col-md-6 col-xl-2.5 emp-field-group"}>
                                <label className="emp-field-label">Employee Type</label>
                                <select
                                  className="form-control form-control-sm"
                                  name="EmployeeType"
                                  value={values.EmployeeType || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                >
                                  <option value="">-- Select Type --</option>
                                  {filterActiveMasters(
                                    (state.EmployeeTypeMaster && state.EmployeeTypeMaster.length > 0)
                                      ? state.EmployeeTypeMaster
                                      : [
                                          { Id: 1, Name: "Ordinary" },
                                          { Id: 2, Name: "Special" },
                                          { Id: 3, Name: "Permanent" },
                                          { Id: 4, Name: "Temporary" },
                                          { Id: 5, Name: "Contract" },
                                          { Id: 6, Name: "Daily Wage" }
                                        ],
                                    values.EmployeeType
                                  ).map((t) => (
                                    <option key={t.Id || t.Name} value={t.Name || t.Id}>
                                      {t.Name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* 6. Father Name */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">Father Name</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="FatherName"
                                  value={values.FatherName || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="Father Name"
                                />
                              </div>

                              {/* 7. Employee Code */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-1.5 emp-field-group">
                                <label className="emp-field-label">Employee Code</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="EmployeeCode"
                                  value={values.EmployeeCode !== undefined && values.EmployeeCode !== null ? values.EmployeeCode : (values.CardNo || "")}
                                  onChange={(e) => {
                                    handleChange(e);
                                    if (setFieldValue) setFieldValue("CardNo", e.target.value);
                                  }}
                                  onBlur={handleBlur}
                                  disabled={isAutoEmpCode}
                                  placeholder={isAutoEmpCode ? "Auto generated" : "Employee Code"}
                                />
                              </div>

                              {/* 8. Sex */}
                              <div className="col-6 col-sm-4 col-md-2 col-xl-1 emp-field-group">
                                <label className="emp-field-label">Sex</label>
                                <select className="form-control form-control-sm" name="Sex" value={values.Sex || "M"} onChange={handleChange} onBlur={handleBlur}>
                                  <option value="M">M</option>
                                  <option value="F">F</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>

                              {/* 9. Deferred Pension */}
                              <div className="col-6 col-sm-4 col-md-4 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">Deferred Pension</label>
                                <div className="emp-control-box d-flex align-items-center justify-content-between px-2">
                                  <label htmlFor="IsDeferredPension" className="d-flex align-items-center mb-0 gap-1.5" style={{ cursor: "pointer", userSelect: "none" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="IsDeferredPension"
                                      name="IsDeferredPension"
                                      checked={!!values.IsDeferredPension}
                                      onChange={(e) => {
                                        const val = e.target.checked;
                                        setFieldValue("IsDeferredPension", val);
                                        if (!val) {
                                          setFieldValue("PensionAge", "");
                                          if (values.DateOfBirth && retirementAge) {
                                            setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, retirementAge));
                                          }
                                        } else {
                                          const defaultAge = values.PensionAge || "60";
                                          setFieldValue("PensionAge", defaultAge);
                                          if (values.DateOfBirth) {
                                            setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, defaultAge));
                                          }
                                        }
                                      }}
                                    />
                                    <span style={{ fontSize: "11px", fontWeight: 600, color: values.IsDeferredPension ? "#553fd9" : "#334155" }}>
                                      Deferred
                                    </span>
                                  </label>
                                  {values.IsDeferredPension && (
                                    <div className="d-flex align-items-center gap-1 ms-auto" style={{ lineHeight: 1 }}>
                                      <span className="text-muted" style={{ fontSize: "10.5px", fontWeight: 600 }}>Age:</span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={2}
                                        className="emp-age-pill"
                                        placeholder="60"
                                        name="PensionAge"
                                        value={values.PensionAge || ""}
                                        onChange={(e) => {
                                          const age = e.target.value.replace(/\D/g, "").slice(0, 2);
                                          setFieldValue("PensionAge", age);
                                          if (values.DateOfBirth && age && Number(age) > 0) {
                                            setFieldValue("DateOfRetire", calculateRetirementDate(values.DateOfBirth, age));
                                          }
                                        }}
                                      />
                                      <span className="text-muted" style={{ fontSize: "10px", fontWeight: 600 }}>Yr</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* 10. Date of Birth */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">Date of Birth</label>
                                <div className="position-relative w-100">
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    name="DateOfBirth"
                                    value={values.DateOfBirth ? values.DateOfBirth.split("T")[0] : ""}
                                    onBlur={handleBlur}
                                    onChange={(e) => {
                                      handleChange(e);
                                      const dobVal = e.target.value;
                                      const ageToUse = values.IsDeferredPension && values.PensionAge ? Number(values.PensionAge) : (retirementAge || 58);
                                      if (dobVal) {
                                        const retireDate = calculateRetirementDate(dobVal, ageToUse);
                                        setFieldValue("DateOfRetire", retireDate);
                                      } else {
                                        setFieldValue("DateOfRetire", "");
                                      }
                                    }}
                                    style={currentAge !== "" ? { paddingRight: "46px" } : {}}
                                  />
                                  {currentAge !== "" && (
                                    <span
                                      className="badge px-1.5 py-0.5 fw-bold position-absolute"
                                      style={{
                                        right: "26px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        backgroundColor: "#ede9fe",
                                        border: "1px solid #c4b5fd",
                                        color: "#6d28d9",
                                        fontSize: "10.5px",
                                        borderRadius: "4px",
                                        pointerEvents: "none"
                                      }}
                                      title="Calculated Age"
                                    >
                                      {currentAge}y
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* 11. Date of Retire */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Date of Retire</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm bg-light"
                                  name="DateOfRetire"
                                  value={values.DateOfRetire ? values.DateOfRetire.split("T")[0] : ""}
                                  readOnly
                                  disabled
                                />
                              </div>

                              {/* 12. Bank Payment */}
                              <div className="col-6 col-sm-4 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Bank Payment</label>
                                <div className="emp-control-box">
                                  <label htmlFor="BankPayment" className="d-flex align-items-center mb-0 gap-1.5" style={{ cursor: "pointer", pointerEvents: "auto", width: "100%" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="BankPayment"
                                      name="BankPayment"
                                      checked={!!values.BankPayment}
                                      onChange={(e) => setFieldValue("BankPayment", e.target.checked)}
                                    />
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Bank Pay</span>
                                  </label>
                                </div>
                              </div>

                              {/* 13. Country */}
                              <div className="col-6 col-sm-4 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Country</label>
                                <select className="form-control form-control-sm" name="F_CountryMaster" value={values.F_CountryMaster || 1} onChange={handleChange} onBlur={handleBlur}>
                                  {filterActiveMasters(state.CountryMaster || [], values.F_CountryMaster || 1).map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                                </select>
                              </div>

                              {/* 14. Permanent Address */}
                              <div className="col-12 col-md-6 col-xl-4 emp-field-group">
                                <label className="emp-field-label">Permanent Address</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="PermanentAddress"
                                  value={values.PermanentAddress || ""}
                                  onChange={(e) => {
                                    handleChange(e);
                                    if (sameAsPermanent) {
                                      setFieldValue("PresentAddress", e.target.value);
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  placeholder="Permanent Address"
                                />
                              </div>

                              {/* 15. Present Address */}
                              <div className="col-12 col-md-6 col-xl-4 emp-field-group">
                                <label className="emp-field-label d-flex justify-content-between align-items-center" style={{ width: "calc(100% - 20px)", pointerEvents: "auto" }}>
                                  <span>Present Address</span>
                                  <span
                                    className="text-primary fw-semibold d-inline-flex align-items-center"
                                    style={{ cursor: "pointer", fontSize: "10.5px" }}
                                    onClick={() => {
                                      const next = !sameAsPermanent;
                                      setSameAsPermanent(next);
                                      if (next) setFieldValue("PresentAddress", values.PermanentAddress || "");
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      id="sameAsPermanent"
                                      className="form-check-input me-1 mt-0"
                                      checked={sameAsPermanent}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSameAsPermanent(checked);
                                        if (checked) {
                                          setFieldValue("PresentAddress", values.PermanentAddress || "");
                                        }
                                      }}
                                      style={{ cursor: "pointer", width: "12px", height: "12px" }}
                                    />
                                    Same as Permanent
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="PresentAddress"
                                  value={values.PresentAddress || ""}
                                  onChange={(e) => {
                                    handleChange(e);
                                    if (sameAsPermanent && e.target.value !== values.PermanentAddress) {
                                      setSameAsPermanent(false);
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  placeholder="Present Address"
                                />
                              </div>

                              {/* 16. Mobile No */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Mobile No.</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="MobileNo"
                                  maxLength={10}
                                  placeholder="10-digit Mobile"
                                  value={values.MobileNo || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setFieldValue("MobileNo", val);
                                  }}
                                  onBlur={handleBlur}
                                />
                                {errors.MobileNo && <div className="text-danger fs-11 mt-1">{errors.MobileNo}</div>}
                              </div>

                              {/* 17. Email */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Email</label>
                                <input
                                  type="email"
                                  className="form-control form-control-sm"
                                  name="Email"
                                  placeholder="Email Address"
                                  value={values.Email || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {errors.Email && <div className="text-danger fs-11 mt-1">{errors.Email}</div>}
                              </div>

                              {/* 18. Ed Qualification */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Ed. Qualification</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="EdQualification"
                                  list="educationOptions"
                                  placeholder="Qualification"
                                  value={values.EdQualification || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                <datalist id="educationOptions">
                                  <option value="Below 10th" />
                                  <option value="10th (Secondary)" />
                                  <option value="12th (Higher Secondary)" />
                                  <option value="ITI / Diploma" />
                                  <option value="Graduate (BA/BSc/BCom/BTech)" />
                                  <option value="Post Graduate (MA/MSc/MCom/MBA)" />
                                  <option value="Illiterate" />
                                </datalist>
                              </div>

                              {/* 19. Handicap Checkbox */}
                              <div className="col-6 col-sm-4 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Handicap</label>
                                <div className="emp-control-box">
                                  <label htmlFor="IsHandicap" className="d-flex align-items-center mb-0 gap-1.5" style={{ cursor: "pointer", pointerEvents: "auto", width: "100%" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="IsHandicap"
                                      name="IsHandicap"
                                      checked={!!values.IsHandicap}
                                      onChange={(e) => {
                                        const val = e.target.checked;
                                        setFieldValue("IsHandicap", val);
                                        if (!val) {
                                          setFieldValue("HandicapCategory", "");
                                        }
                                      }}
                                    />
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Handicap</span>
                                  </label>
                                </div>
                              </div>

                              {/* Conditional: Handicap Category */}
                              {values.IsHandicap && (
                                <div className="col-6 col-sm-4 col-md-3 col-xl-2 emp-field-group">
                                  <label className="emp-field-label">Handicap Category</label>
                                  <select
                                    className="form-control form-control-sm"
                                    name="HandicapCategory"
                                    value={values.HandicapCategory || ""}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  >
                                    <option value="">-- Category --</option>
                                    <option value="Locomotor Disability">Locomotor Disability</option>
                                    <option value="Visual Impairment">Visual Impairment</option>
                                    <option value="Hearing Impairment">Hearing Impairment</option>
                                    <option value="Mental Illness / Intellectual">Mental Illness / Intellectual</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </div>
                              )}

                              {/* 20. Working Status */}
                              <div className="col-6 col-sm-4 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Working Status</label>
                                <select
                                  className="form-control form-control-sm"
                                  name="Status"
                                  value={values.Status || values.WorkingStatus || "Working"}
                                  onChange={(e) => {
                                    handleChange(e);
                                    setFieldValue("WorkingStatus", e.target.value);
                                  }}
                                  onBlur={handleBlur}
                                >
                                  <option value="Working">Working</option>
                                  <option value="Left">Left</option>
                                  <option value="Suspended">Suspended</option>
                                  <option value="Retired">Retired</option>
                                </select>
                              </div>

                              {/* 21. Aadhar Number */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Aadhar Number</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="AadharNumber"
                                  maxLength={12}
                                  placeholder="12-digit Aadhar"
                                  value={values.AadharNumber || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                                    setFieldValue("AadharNumber", val);
                                    if (val) {
                                      if (!values.NameInAadhar) setFieldValue("NameInAadhar", values.Name || "");
                                    } else {
                                      if (values.NameInAadhar === (values.Name || "")) setFieldValue("NameInAadhar", "");
                                    }
                                  }}
                                  onBlur={handleBlur}
                                />
                              </div>

                              {/* 22. Name in Aadhar */}
                              <div className="col-12 col-sm-6 col-md-4 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Name in Aadhar</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="NameInAadhar"
                                  placeholder="Name as on Aadhar"
                                  value={values.NameInAadhar || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>

                              {/* 23. Marital Status */}
                              <div className="col-6 col-sm-3 col-md-2 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Marital Status</label>
                                <div className="emp-control-box">
                                  <label htmlFor="MaritalStatus" className="d-flex align-items-center mb-0 gap-1.5" style={{ cursor: "pointer", pointerEvents: "auto", width: "100%" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="MaritalStatus"
                                      name="MaritalStatus"
                                      checked={!!values.MaritalStatus}
                                      onChange={(e) => {
                                        const val = e.target.checked;
                                        setFieldValue("MaritalStatus", val);
                                        if (val && values.Married === "Unmarried") {
                                          setFieldValue("Married", "Married");
                                        }
                                      }}
                                    />
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Married</span>
                                  </label>
                                </div>
                              </div>

                              {/* 24. Married */}
                              <div className="col-6 col-sm-3 col-md-2 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Married</label>
                                <select className="form-control form-control-sm" name="Married" value={values.Married || "Unmarried"} onChange={handleChange} onBlur={handleBlur}>
                                  <option value="Unmarried">Unmarried</option>
                                  <option value="Married">Married</option>
                                  <option value="Widowed">Widowed</option>
                                  <option value="Divorced">Divorced</option>
                                </select>
                              </div>

                              {/* 25. Refund File Date */}
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Refund File Date</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  name="RefundFileDate"
                                  value={values.RefundFileDate ? values.RefundFileDate.split("T")[0] : ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>

                              {/* 26. Refunded */}
                              <div className="col-6 col-sm-3 col-md-2 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Refund Status</label>
                                <div className="emp-control-box">
                                  <label htmlFor="Refunded" className="d-flex align-items-center mb-0 gap-1.5" style={{ cursor: "pointer", pointerEvents: "auto", width: "100%" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id="Refunded"
                                      name="Refunded"
                                      checked={!!values.Refunded}
                                      onChange={(e) => setFieldValue("Refunded", e.target.checked)}
                                    />
                                    <span style={{ fontSize: "11px", fontWeight: 600 }}>Refunded</span>
                                  </label>
                                </div>
                              </div>

                              {/* 27. Gratuity Paid Date */}
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Gratuity Paid Date</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  name="GratuityPaidDate"
                                  value={values.GratuityPaidDate ? values.GratuityPaidDate.split("T")[0] : ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </div>

                              {/* 28. Remark */}
                              <div className="col-12 col-md-6 col-xl-6 emp-field-group">
                                <label className="emp-field-label">Remark</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="Remark"
                                  value={values.Remark || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="Extra remarks"
                                />
                              </div>

                              {/* 29. Employee Photo */}
                              <div className="col-6 col-md-3 col-xl-3 emp-field-group">
                                <label className="emp-field-label">Employee Photo</label>
                                <div className="d-flex align-items-center gap-1 p-1 rounded emp-media-box" style={{ background: "#ffffff", height: "33px" }}>
                                  <div style={{ width: "26px", height: "25px", borderRadius: "3px", border: "1px solid #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#f8fafc", flexShrink: 0 }}>
                                    {values.PhotoPreview || values.Photo ? (
                                      <img
                                        src={values.PhotoPreview || (values.Photo.startsWith("data:") || values.Photo.startsWith("http") ? values.Photo : `${API_WEB_URLS.IMAGEURL}${values.Photo}`)}
                                        alt="Photo"
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <i className="fa fa-user text-muted" style={{ fontSize: "12px" }} />
                                    )}
                                  </div>
                                  <div className="flex-grow-1 d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="form-control form-control-sm py-0 px-1"
                                      style={{ height: "24px", fontSize: "10px", border: "1px solid #cbd5e1" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setFieldValue("PhotoFile", file);
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            setFieldValue("PhotoPreview", reader.result);
                                            setFieldValue("Photo", reader.result);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                    {(values.PhotoPreview || values.Photo) && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-xs py-0 px-1"
                                        style={{ height: "24px", fontSize: "10px", lineHeight: 1 }}
                                        title="Remove Photo"
                                        onClick={() => {
                                          setFieldValue("PhotoFile", null);
                                          setFieldValue("PhotoPreview", "");
                                          setFieldValue("Photo", "");
                                        }}
                                      >
                                        <i className="fa fa-trash" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* 30. Employee Signature */}
                              <div className="col-6 col-md-3 col-xl-3 emp-field-group">
                                <label className="emp-field-label">Employee Signature</label>
                                <div className="d-flex align-items-center gap-1 p-1 rounded emp-media-box" style={{ background: "#ffffff", height: "33px" }}>
                                  <div style={{ width: "38px", height: "25px", borderRadius: "3px", border: "1px solid #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#f8fafc", flexShrink: 0 }}>
                                    {values.SignaturePreview || values.Signature ? (
                                      <img
                                        src={values.SignaturePreview || (values.Signature.startsWith("data:") || values.Signature.startsWith("http") ? values.Signature : `${API_WEB_URLS.IMAGEURL}${values.Signature}`)}
                                        alt="Signature"
                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                      />
                                    ) : (
                                      <i className="fa fa-pencil text-muted" style={{ fontSize: "12px" }} />
                                    )}
                                  </div>
                                  <div className="flex-grow-1 d-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="form-control form-control-sm py-0 px-1"
                                      style={{ height: "24px", fontSize: "10px", border: "1px solid #cbd5e1" }}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setFieldValue("SignatureFile", file);
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            setFieldValue("SignaturePreview", reader.result);
                                            setFieldValue("Signature", reader.result);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                    {(values.SignaturePreview || values.Signature) && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-xs py-0 px-1"
                                        style={{ height: "24px", fontSize: "10px", lineHeight: 1 }}
                                        title="Remove Signature"
                                        onClick={() => {
                                          setFieldValue("SignatureFile", null);
                                          setFieldValue("SignaturePreview", "");
                                          setFieldValue("Signature", "");
                                        }}
                                      >
                                        <i className="fa fa-trash" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        )}

                        {/* ════════════════════════════════════════════════════════════════════════
                            TAB 2: BASIC (Salary & Compliance)
                            ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "salary" && (
                        <div className="tab-pane-content emp-compact-tab">
                          
                          {/* 1. Top Salary Grid */}
                          <div className="table-responsive mb-1" style={{ maxHeight: "140px" }}>
                            <table className="table table-bordered table-sm align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: 30 }} className="text-center">#</th>
                                  <th>Basic Salary</th>
                                  <th>Effective Date</th>
                                  <th style={{ minWidth: 280 }}>Basic Type *</th>
                                  <th style={{ width: 70 }} className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salaryRows.map((row, i) => (
                                  <tr key={i}>
                                    <td className="align-middle text-center">{i + 1}</td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="form-control form-control-sm"
                                        placeholder="0.00"
                                        value={row.BasicSalary}
                                        onChange={(e) => changeSalaryRow(i, "BasicSalary", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                        onChange={(e) => changeSalaryRow(i, "EffectiveDate", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {(state.BasicTypeMaster && state.BasicTypeMaster.length > 0
                                          ? state.BasicTypeMaster
                                          : [
                                              { Id: 1, Name: "Monthly" },
                                              { Id: 2, Name: "Per Day" },
                                              { Id: 3, Name: "Hourly" },
                                              { Id: 4, Name: "Piece Rate" }
                                            ]
                                        ).map((bt) => (
                                          <div className="form-check form-check-inline mb-0" key={bt.Id}>
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name={`basicType_${i}`}
                                              id={`basicType_${bt.Id}_${i}`}
                                              value={bt.Id}
                                              checked={Number(row.F_BasicTypeMaster) === Number(bt.Id) || (!row.F_BasicTypeMaster && Number(bt.Id) === 1)}
                                              onChange={() => changeSalaryRow(i, "F_BasicTypeMaster", Number(bt.Id))}
                                            />
                                            <label className="form-check-label" htmlFor={`basicType_${bt.Id}_${i}`}>
                                              {bt.Name}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <ActionCell
                                      index={i}
                                      rows={salaryRows}
                                      onAdd={() => {
                                        const monthly = state.BasicTypeMaster?.find(b => b.Id === 1 || String(b.Name).toLowerCase().includes("month"));
                                        const defaultId = monthly ? monthly.Id : 1;
                                        const effDate = getPrimaryEffectiveDate();
                                        addRow(setSalaryRows, () => ({ ...emptySalaryRow(defaultId), EffectiveDate: effDate }));
                                      }}
                                      onRemove={(idx) => removeRow(setSalaryRows, idx)}
                                    />
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* 2. Compliance Section (ESI & PF) */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#f8fbff", borderRadius: 6, padding: "6px 8px" }}>
                            
                            {/* ESI Row */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box">
                                  <label htmlFor="ESIApplicable" className="form-check-label fw-bold text-primary" style={{ cursor: "pointer" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input me-1"
                                      id="ESIApplicable"
                                      checked={!!values.ESIApplicable}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setFieldValue("ESIApplicable", isChecked);
                                        if (isChecked) {
                                          const effDate = getPrimaryEffectiveDate();
                                          if (effDate) {
                                            setFieldValue("ESIJoiningDate", effDate);
                                          }
                                        } else {
                                          setFieldValue("ESIJoiningDate", "");
                                        }
                                      }}
                                    />
                                    ESI Applicable
                                  </label>
                                </div>
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">ESI No</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="ESINo"
                                  placeholder="ESI No"
                                  value={values.ESINo || ""}
                                  onChange={handleChange}
                                  onBlur={(e) => {
                                    handleBlur(e);
                                    const val = e.target.value?.trim();
                                    if (checkDuplicateESI && val) {
                                      const dup = (firmEmployees || []).find(emp =>
                                        String(emp.Id) !== String(state.id) &&
                                        emp.ESINo &&
                                        String(emp.ESINo).trim().toLowerCase() === val.toLowerCase()
                                      );
                                      if (dup) {
                                        const empDesc = `${dup.Name || "Employee"}${dup.EmployeeCode ? ` (${dup.EmployeeCode})` : ""}`;
                                        toast.warn(`Warning: An employee [${empDesc}] already exists with this ESI number. Duplicate ESI entry is not allowed.`);
                                      }
                                    }
                                  }}
                                />
                              </div>
                              {values.ESIApplicable && (
                                <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                  <label className="emp-field-label">ESI Join Date</label>
                                  <input type="date" className="form-control form-control-sm" name="ESIJoiningDate" value={values.ESIJoiningDate ? values.ESIJoiningDate.split("T")[0] : ""} onChange={handleChange} />
                                </div>
                              )}
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box">
                                  <label className="form-check-label fw-semibold" htmlFor="Accident" style={{ cursor: "pointer" }}>
                                    <input
                                      className="form-check-input me-1"
                                      type="checkbox"
                                      id="Accident"
                                      checked={!!values.Accident}
                                      onChange={e => {
                                        const isChecked = e.target.checked;
                                        setFieldValue("Accident", isChecked);
                                        if (isChecked && (!accidentRows || accidentRows.length === 0)) {
                                          setAccidentRows([emptyAccidentRow()]);
                                        }
                                      }}
                                    />
                                    Accident
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Inline Date of Accident History (when Accident is checked) */}
                            {values.Accident && (
                              <div className="col-12 mt-2 mb-2 p-2 rounded bg-light border border-warning-subtle">
                                <div className="d-flex align-items-center justify-content-between mb-1 pb-1 border-bottom">
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-danger">
                                      <i className="fa fa-ambulance me-1" /> Date of Accident
                                    </span>
                                    <span className="text-muted fw-semibold" style={{ fontSize: 12 }}>
                                      Record accident dates & remarks
                                    </span>
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-xs py-0 px-2"
                                      onClick={() => setActiveTab("accidentHistory")}
                                      title="Open full Accident History tab"
                                    >
                                      Open Accident History Tab &rarr;
                                    </button>
                                  </div>
                                </div>
                                <div className="table-responsive" style={{ maxHeight: "200px" }}>
                                  <table className="table table-bordered table-sm table-hover align-middle mb-1 bg-white">
                                    <thead className="table-secondary">
                                      <tr>
                                        <th style={{ width: 40 }} className="text-center">#</th>
                                        <th style={{ width: 220 }}>Date of Accident *</th>
                                        <th>Remark / Details</th>
                                        <th style={{ width: 80 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(accidentRows && accidentRows.length > 0 ? accidentRows : [emptyAccidentRow()]).map((row, i) => (
                                        <tr key={i}>
                                          <td className="text-center fw-bold">{i + 1}</td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={row.AccidentDate ? row.AccidentDate.split("T")[0] : ""}
                                              onChange={(e) => changeRow(setAccidentRows, i, "AccidentDate", e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              placeholder="Enter accident reason / remarks"
                                              value={row.Remark || ""}
                                              onChange={(e) => changeRow(setAccidentRows, i, "Remark", e.target.value)}
                                            />
                                          </td>
                                          <ActionCell
                                            index={i}
                                            rows={accidentRows && accidentRows.length > 0 ? accidentRows : [emptyAccidentRow()]}
                                            onAdd={() => addRow(setAccidentRows, emptyAccidentRow)}
                                            onRemove={(idx) => removeRow(setAccidentRows, idx)}
                                          />
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* PF Section */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box">
                                  <label htmlFor="PFApplicable" className="form-check-label fw-bold text-primary" style={{ cursor: "pointer" }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input me-1"
                                      id="PFApplicable"
                                      checked={!!values.PFApplicable}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setFieldValue("PFApplicable", isChecked);
                                        if (isChecked) {
                                          if (!values.PensionApplicable) {
                                            setFieldValue("PensionApplicable", true);
                                          }
                                          const effDate = getPrimaryEffectiveDate();
                                          if (effDate) {
                                            setFieldValue("PFJoinDate", effDate);
                                          }
                                        } else {
                                          setFieldValue("PFJoinDate", "");
                                        }
                                      }}
                                    />
                                    PF Applicable
                                  </label>
                                </div>
                              </div>
                              {values.PFApplicable && (
                                <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                  <label className="emp-field-label">PF Join Date</label>
                                  <input type="date" className="form-control form-control-sm" name="PFJoinDate" value={values.PFJoinDate ? values.PFJoinDate.split("T")[0] : ""} onChange={handleChange} />
                                </div>
                              )}
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box d-flex flex-wrap align-items-center gap-2">
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="EmplrPFOverLimit" checked={!!values.EmplrPFOverLimit} onChange={e => setFieldValue("EmplrPFOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="EmplrPFOverLimit">Emplr Over Lim</label></div>
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="EmpleeDedOverLimit" checked={!!values.EmpleeDedOverLimit} onChange={e => setFieldValue("EmpleeDedOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="EmpleeDedOverLimit">Emplee Over Lim</label></div>
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PensionApplicable" checked={!!values.PensionApplicable} onChange={e => setFieldValue("PensionApplicable", e.target.checked)} /><label className="form-check-label" htmlFor="PensionApplicable">Pension</label></div>
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="PensionOverLimit" checked={!!values.PensionOverLimit} onChange={e => setFieldValue("PensionOverLimit", e.target.checked)} /><label className="form-check-label" htmlFor="PensionOverLimit">Pension Over Lim</label></div>
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="HigherPension" checked={!!values.HigherPension} onChange={e => setFieldValue("HigherPension", e.target.checked)} /><label className="form-check-label" htmlFor="HigherPension">Higher Pension</label></div>
                                </div>
                              </div>
                            </div>

                            {/* PF Row 1: PF No | UAN | Extra PF | Calc Type | Fixed PF Salary */}
                            <div className="row gx-2 gy-1 align-items-center mb-1">
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">PF No</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="PFNo"
                                  placeholder="PF No"
                                  value={values.PFNo || ""}
                                  onChange={handleChange}
                                  onBlur={(e) => {
                                    handleBlur(e);
                                    const val = e.target.value?.trim();
                                    if (checkDuplicatePF && val) {
                                      const dup = (firmEmployees || []).find(emp =>
                                        String(emp.Id) !== String(state.id) &&
                                        emp.PFNo &&
                                        String(emp.PFNo).trim().toLowerCase() === val.toLowerCase()
                                      );
                                      if (dup) {
                                        const empDesc = `${dup.Name || "Employee"}${dup.EmployeeCode ? ` (${dup.EmployeeCode})` : ""}`;
                                        toast.warn(`Warning: An employee [${empDesc}] already exists with this PF number. Duplicate PF entry is not allowed.`);
                                      }
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-12 col-sm-6 col-md-4 col-xl-3.5 emp-field-group">
                                <label className="emp-field-label">UAN</label>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${errors.UAN && (touched?.UAN || !!errors.UAN) ? "is-invalid" : ""}`}
                                  name="UAN"
                                  id="UAN"
                                  placeholder="Max 12 digits"
                                  maxLength={12}
                                  value={values.UAN || ""}
                                  onChange={(e) => {
                                    const numericVal = e.target.value.replace(/\D/g, "").slice(0, 12);
                                    setFieldValue("UAN", numericVal);
                                  }}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pasteData = (e.clipboardData || window.clipboardData).getData("text") || "";
                                    const numericVal = pasteData.replace(/\D/g, "").slice(0, 12);
                                    setFieldValue("UAN", numericVal);
                                  }}
                                  onKeyDown={(e) => {
                                    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key)) return;
                                    if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                                    if (!/^[0-9]$/.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  title="UAN must be maximum 12 digits"
                                />
                                {errors.UAN && <div className="text-danger fs-11 mt-1">{errors.UAN}</div>}
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">Fixed PF Salary</label>
                                <input type="number" step="0.01" className="form-control form-control-sm" name="FixedPFSalary" value={values.FixedPFSalary || ""} onChange={handleChange} />
                              </div>
                            </div>

                            {/* PF Row 2: Old Member, Prev PF, Old UAN, Exit Date, PMRPY, PMGKY, ABRY */}
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-auto emp-field-group">
                                <label className="emp-field-label">Old Member</label>
                                <div className="emp-control-box gap-2">
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="OldMemberPF" checked={!!values.OldMemberPF} onChange={e => setFieldValue("OldMemberPF", e.target.checked)} /><label className="form-check-label" htmlFor="OldMemberPF">PF</label></div>
                                  <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id="OldMemberPension" checked={!!values.OldMemberPension} onChange={e => setFieldValue("OldMemberPension", e.target.checked)} /><label className="form-check-label" htmlFor="OldMemberPension">Pension</label></div>
                                </div>
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Prev PF No</label>
                                <input type="text" className="form-control form-control-sm" name="PrevPFNo" value={values.PrevPFNo || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">Old UAN</label>
                                <input
                                  type="text"
                                  className={`form-control form-control-sm ${errors.OldUAN && (touched?.OldUAN || !!errors.OldUAN) ? "is-invalid" : ""}`}
                                  name="OldUAN"
                                  id="OldUAN"
                                  placeholder="Max 12 digits"
                                  maxLength={12}
                                  value={values.OldUAN || ""}
                                  onChange={(e) => {
                                    const numericVal = e.target.value.replace(/\D/g, "").slice(0, 12);
                                    setFieldValue("OldUAN", numericVal);
                                  }}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pasteData = (e.clipboardData || window.clipboardData).getData("text") || "";
                                    const numericVal = pasteData.replace(/\D/g, "").slice(0, 12);
                                    setFieldValue("OldUAN", numericVal);
                                  }}
                                  onKeyDown={(e) => {
                                    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key)) return;
                                    if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;
                                    if (!/^[0-9]$/.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  title="Old UAN must be maximum 12 digits"
                                />
                                {errors.OldUAN && <div className="text-danger fs-11 mt-1">{errors.OldUAN}</div>}
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Exit Date</label>
                                <input type="date" className="form-control form-control-sm" name="DateOfExitPr" value={values.DateOfExitPr ? values.DateOfExitPr.split("T")[0] : ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto emp-field-group ms-auto">
                                <div className="emp-control-box gap-2">
                                  <div className="form-check mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="PMRPY"
                                      name="PMRPY"
                                      checked={!!values.PMRPY}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFieldValue("PMRPY", checked);
                                        if (checked) {
                                          setFieldValue("PMGKY", false);
                                          setFieldValue("ABRY", false);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          if (e.target.checked || values.PMRPY || values.PMGKY || values.ABRY) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const nextField = document.querySelector('[name="WeeklyHoliday"]') || document.getElementById("Is26DaysWorking");
                                            if (nextField) nextField.focus();
                                          }
                                        }
                                      }}
                                    />
                                    <label className="form-check-label fw-semibold" htmlFor="PMRPY">PMRPY</label>
                                  </div>
                                  <div className="form-check mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="PMGKY"
                                      name="PMGKY"
                                      checked={!!values.PMGKY}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFieldValue("PMGKY", checked);
                                        if (checked) {
                                          setFieldValue("PMRPY", false);
                                          setFieldValue("ABRY", false);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          if (e.target.checked || values.PMRPY || values.PMGKY || values.ABRY) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const nextField = document.querySelector('[name="WeeklyHoliday"]') || document.getElementById("Is26DaysWorking");
                                            if (nextField) nextField.focus();
                                          }
                                        }
                                      }}
                                    />
                                    <label className="form-check-label fw-semibold" htmlFor="PMGKY">PMGKY</label>
                                  </div>
                                  <div className="form-check mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="ABRY"
                                      name="ABRY"
                                      checked={!!values.ABRY}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFieldValue("ABRY", checked);
                                        if (checked) {
                                          setFieldValue("PMRPY", false);
                                          setFieldValue("PMGKY", false);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          if (e.target.checked || values.PMRPY || values.PMGKY || values.ABRY) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const nextField = document.querySelector('[name="WeeklyHoliday"]') || document.getElementById("Is26DaysWorking");
                                            if (nextField) nextField.focus();
                                          }
                                        }
                                      }}
                                    />
                                    <label className="form-check-label fw-semibold" htmlFor="ABRY">ABRY</label>
                                  </div>
                                </div>
                              </div>
                            </div>

                          </div>

                          {/* 3. Bank Details & Holiday Section */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#fdfefe", borderRadius: 6, padding: "6px 8px" }}>
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-6 col-sm-4 col-md-2 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Holiday</label>
                                <select className="form-control form-control-sm" name="WeeklyHoliday" value={values.WeeklyHoliday || "Sunday"} onChange={handleChange}>
                                  <option value="Sunday">Sunday</option>
                                  <option value="Monday">Monday</option>
                                  <option value="Tuesday">Tuesday</option>
                                  <option value="Wednesday">Wednesday</option>
                                  <option value="Thursday">Thursday</option>
                                  <option value="Friday">Friday</option>
                                  <option value="Saturday">Saturday</option>
                                </select>
                              </div>
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box">
                                  <label className="form-check-label fw-bold text-dark" htmlFor="Is26DaysWorking" style={{ cursor: "pointer" }}>
                                    <input
                                      className="form-check-input me-1"
                                      type="checkbox"
                                      id="Is26DaysWorking"
                                      name="Is26DaysWorking"
                                      checked={!!values.Is26DaysWorking}
                                      onChange={(e) => setFieldValue("Is26DaysWorking", e.target.checked)}
                                    />
                                    26 Days Working
                                  </label>
                                </div>
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Bank Name</label>
                                <input type="text" className="form-control form-control-sm" name="BankName" placeholder="Bank Name" value={values.BankName || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Branch</label>
                                <input type="text" className="form-control form-control-sm" name="BankBranch" placeholder="Branch" value={values.BankBranch || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-2 col-xl-1.5 emp-field-group">
                                <label className="emp-field-label">IFSC</label>
                                <input type="text" className="form-control form-control-sm" name="IFSCCode" placeholder="IFSC Code" value={values.IFSCCode || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">A/c No.</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="BankAccountNo"
                                  placeholder="Account No"
                                  value={values.BankAccountNo || ""}
                                  onChange={(e) => {
                                    const acc = e.target.value;
                                    setFieldValue("BankAccountNo", acc);
                                    if (acc) {
                                      if (!values.NameAsBank) setFieldValue("NameAsBank", values.Name || "");
                                    } else {
                                      if (values.NameAsBank === (values.Name || "")) setFieldValue("NameAsBank", "");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">In Bank</label>
                                <input type="text" className="form-control form-control-sm" name="NameAsBank" placeholder="Name as in Bank" value={values.NameAsBank || ""} onChange={handleChange} />
                              </div>
                            </div>
                          </div>

                          {/* 4. Identity & Documents Section (PAN, DL, Passport, Intl. Emp) */}
                          <div className="card mb-1 border shadow-none" style={{ background: "#f8f9fa", borderRadius: 6, padding: "6px 8px" }}>
                            {/* Row 1: PAN & Driving Licence */}
                            <div className="row gx-2 gy-1 align-items-center mb-1 pb-1 border-bottom">
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">PAN No</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm text-uppercase"
                                  name="PanCardNo"
                                  placeholder="PAN No"
                                  maxLength={10}
                                  value={values.PanCardNo || ""}
                                  onChange={(e) => {
                                    const pan = e.target.value.toUpperCase();
                                    setFieldValue("PanCardNo", pan);
                                    if (pan) {
                                      if (!values.NameAsPerPan) setFieldValue("NameAsPerPan", values.Name || "");
                                    } else {
                                      if (values.NameAsPerPan === (values.Name || "")) setFieldValue("NameAsPerPan", "");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">In PAN</label>
                                <input type="text" className="form-control form-control-sm" name="NameAsPerPan" placeholder="PAN Name" value={values.NameAsPerPan || ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">DL No.</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="DrivingLicenceNo"
                                  placeholder="DL No"
                                  value={values.DrivingLicenceNo || ""}
                                  onChange={(e) => {
                                    const dl = e.target.value;
                                    setFieldValue("DrivingLicenceNo", dl);
                                    if (dl) {
                                      if (!values.NameAsPerDL) setFieldValue("NameAsPerDL", values.Name || "");
                                    } else {
                                      if (values.NameAsPerDL === (values.Name || "")) setFieldValue("NameAsPerDL", "");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">DL Expiry</label>
                                <input type="date" className="form-control form-control-sm" name="DLExpiryDate" value={values.DLExpiryDate ? values.DLExpiryDate.split("T")[0] : ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-4 col-xl-4 emp-field-group">
                                <label className="emp-field-label">In DL</label>
                                <input type="text" className="form-control form-control-sm" name="NameAsPerDL" placeholder="DL Name" value={values.NameAsPerDL || ""} onChange={handleChange} />
                              </div>
                            </div>

                            {/* Row 2: Passport & International Employee */}
                            <div className="row gx-2 gy-1 align-items-center">
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2 emp-field-group">
                                <label className="emp-field-label">Passport No</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  name="PassportNo"
                                  placeholder="Passport No"
                                  value={values.PassportNo || ""}
                                  onChange={(e) => {
                                    const pass = e.target.value;
                                    setFieldValue("PassportNo", pass);
                                    if (pass) {
                                      if (!values.PassportName) setFieldValue("PassportName", values.Name || "");
                                    } else {
                                      if (values.PassportName === (values.Name || "")) setFieldValue("PassportName", "");
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-6 col-sm-3 col-md-2 col-xl-1.5 emp-field-group">
                                <label className="emp-field-label">From</label>
                                <input type="date" className="form-control form-control-sm" name="PassportFrom" value={values.PassportFrom ? values.PassportFrom.split("T")[0] : ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-sm-3 col-md-2 col-xl-1.5 emp-field-group">
                                <label className="emp-field-label">Till</label>
                                <input type="date" className="form-control form-control-sm" name="PassportTill" value={values.PassportTill ? values.PassportTill.split("T")[0] : ""} onChange={handleChange} />
                              </div>
                              <div className="col-12 col-sm-6 col-md-3 col-xl-2.5 emp-field-group">
                                <label className="emp-field-label">In Passport</label>
                                <input type="text" className="form-control form-control-sm" name="PassportName" placeholder="Passport Name" value={values.PassportName || ""} onChange={handleChange} />
                              </div>
                              <div className="col-auto emp-field-group">
                                <div className="emp-control-box">
                                  <label className="form-check-label fw-bold" htmlFor="IsInternationalEmployee" style={{ cursor: "pointer" }}>
                                    <input className="form-check-input me-1" type="checkbox" id="IsInternationalEmployee" checked={!!values.IsInternationalEmployee} onChange={e => setFieldValue("IsInternationalEmployee", e.target.checked)} />
                                    Intl. Emp
                                  </label>
                                </div>
                              </div>
                              <div className="col-6 col-sm-4 col-md-2 col-xl-1 emp-field-group">
                                <label className="emp-field-label">Origin</label>
                                <input type="text" className="form-control form-control-sm" name="CountryOrigin" value={values.CountryOrigin || ""} onChange={handleChange} />
                              </div>
                              <div className="col-6 col-sm-4 col-md-2 col-xl-1 emp-field-group">
                                <label className="emp-field-label">Nation</label>
                                <input type="text" className="form-control form-control-sm" name="Nationality" value={values.Nationality || ""} onChange={handleChange} />
                              </div>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          TAB 3: KYC
                          ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "kyc" && (
                        <div className="tab-pane-content">
                          
                          {/* Top KYC Fields Form */}
                          <div className="card mb-2 border shadow-none" style={{ background: "#f8fbff", borderRadius: 6, padding: "6px 8px" }}>
                            <div className="card-body py-1 px-2">
                              <div className="row gx-2 gy-1 align-items-center">
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Voter ID No</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    name="VoterIDNo"
                                    placeholder="Voter ID No"
                                    value={values.VoterIDNo || ""}
                                    onChange={(e) => {
                                      const vid = e.target.value;
                                      setFieldValue("VoterIDNo", vid);
                                      if (vid) {
                                        if (!values.NameAsPerVoterID) setFieldValue("NameAsPerVoterID", values.Name || "");
                                      } else {
                                        if (values.NameAsPerVoterID === (values.Name || "")) setFieldValue("NameAsPerVoterID", "");
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Name in Voter ID</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerVoterID" placeholder="Name as per Voter ID" value={values.NameAsPerVoterID || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Ration Card No</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    name="RationCardNo"
                                    placeholder="Ration Card No"
                                    value={values.RationCardNo || ""}
                                    onChange={(e) => {
                                      const rc = e.target.value;
                                      setFieldValue("RationCardNo", rc);
                                      if (rc) {
                                        if (!values.NameAsPerRationCard) setFieldValue("NameAsPerRationCard", values.Name || "");
                                      } else {
                                        if (values.NameAsPerRationCard === (values.Name || "")) setFieldValue("NameAsPerRationCard", "");
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Name in Ration Card</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerRationCard" placeholder="Name as per Ration Card" value={values.NameAsPerRationCard || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">National Population No</label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    name="NationalPopulationNo"
                                    placeholder="National Population No"
                                    value={values.NationalPopulationNo || ""}
                                    onChange={(e) => {
                                      const npr = e.target.value;
                                      setFieldValue("NationalPopulationNo", npr);
                                      if (npr) {
                                        if (!values.NameAsPerNPR) setFieldValue("NameAsPerNPR", values.Name || "");
                                      } else {
                                        if (values.NameAsPerNPR === (values.Name || "")) setFieldValue("NameAsPerNPR", "");
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Name in NPR</label>
                                  <input type="text" className="form-control form-control-sm" name="NameAsPerNPR" placeholder="Name as per NPR" value={values.NameAsPerNPR || ""} onChange={handleChange} />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 emp-field-group">
                                  <label className="emp-field-label">Opening Balance Salary</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="OpeningBalanceSalary" value={values.OpeningBalanceSalary || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Nominee Table (With Photo Column) */}
                          <div className="mb-2">
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Nominee Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>NomineeName</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th>Birth Date</th>
                                    <th>Amount (%)</th>
                                    <th>Parent Name</th>
                                    <th>Aadhar No</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {nomineeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Nominee"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setNomineeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setNomineeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NomineeName} onChange={(e) => changeRow(setNomineeRows, i, "NomineeName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setNomineeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setNomineeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {filterActiveMasters(state.RelationshipMaster, row.F_RelationshipMaster).map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.BirthDate ? row.BirthDate.split("T")[0] : ""} onChange={(e) => changeRow(setNomineeRows, i, "BirthDate", e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setNomineeRows, i, "Amount", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.ParentName} onChange={(e) => changeRow(setNomineeRows, i, "ParentName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setNomineeRows, i, "AadharNo", e.target.value)} /></td>
                                      <ActionCell index={i} rows={nomineeRows} onAdd={() => addRow(setNomineeRows, emptyNomineeRow)} onRemove={(idx) => removeRow(setNomineeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Relative / Family Table */}
                          <div>
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Relative / Family Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>RelativeName</th>
                                    <th>DateofBirth</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th style={{ width: 70 }} className="text-center">Living with</th>
                                    <th style={{ width: 60 }} className="text-center">For ESI</th>
                                    <th>Aadhar</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {relativeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Relative"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setRelativeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setRelativeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.RelativeName} onChange={(e) => changeRow(setRelativeRows, i, "RelativeName", e.target.value)} /></td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.DateOfBirth ? row.DateOfBirth.split("T")[0] : ""} onChange={(e) => changeRow(setRelativeRows, i, "DateOfBirth", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setRelativeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setRelativeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {filterActiveMasters(state.RelationshipMaster, row.F_RelationshipMaster).map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td className="text-center align-middle">
                                        <input type="checkbox" checked={row.LivingWith === 1 || row.LivingWith === true} onChange={(e) => changeRow(setRelativeRows, i, "LivingWith", e.target.checked ? 1 : 0)} />
                                      </td>
                                      <td className="text-center align-middle">
                                        <input type="checkbox" checked={row.ForESI === 1 || row.ForESI === true} onChange={(e) => changeRow(setRelativeRows, i, "ForESI", e.target.checked ? 1 : 0)} />
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setRelativeRows, i, "AadharNo", e.target.value)} /></td>
                                      <ActionCell index={i} rows={relativeRows} onAdd={() => addRow(setRelativeRows, emptyRelativeRow)} onRemove={(idx) => removeRow(setRelativeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          TAB 4: PENSION NOMINEE
                          ════════════════════════════════════════════════════════════════════════ */}
                      {activeTab === "pensionNominee" && (
                        <div className="tab-pane-content">
                          
                          {/* Pension Nominee Table (With Photo Column) */}
                          <div className="mb-2">
                            <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "11.5px" }}>Pension Nominee Details</h6>
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 30 }} className="text-center">#</th>
                                    <th style={{ width: 50 }} className="text-center">Photo</th>
                                    <th>NomineeName</th>
                                    <th>Address</th>
                                    <th>Relationship</th>
                                    <th>Birth Date</th>
                                    <th>Amount Part</th>
                                    <th>Parent Name</th>
                                    <th>Aadhar No</th>
                                    <th>Name in Hindi</th>
                                    <th>Address Hindi</th>
                                    <th>Rel in Hindi</th>
                                    <th style={{ width: 70 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pensionNomineeRows.map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td className="align-middle text-center" style={{ minWidth: 50 }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                          {row.Photo ? (
                                            <div className="position-relative" style={{ width: 28, height: 28 }}>
                                              <img
                                                src={row.Photo.startsWith("data:") || row.Photo.startsWith("http") ? row.Photo : `${API_WEB_URLS.IMAGEURL}${row.Photo}`}
                                                alt="Pension Nominee"
                                                style={{ width: 28, height: 28, objectFit: "cover", borderRadius: "3px", border: "1px solid #ccc" }}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs position-absolute"
                                                style={{ top: -5, right: -6, padding: "0 3px", fontSize: "8px", borderRadius: "50%", lineHeight: 1 }}
                                                title="Remove photo"
                                                onClick={() => changeRow(setPensionNomineeRows, i, "Photo", "")}
                                              >
                                                ×
                                              </button>
                                            </div>
                                          ) : (
                                            <label className="btn btn-outline-primary btn-xs mb-0 py-0 px-1" style={{ fontSize: "10px", cursor: "pointer" }} title="Upload Photo">
                                              <i className="fa fa-camera" />
                                              <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                  const file = e.target.files[0];
                                                  if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                      changeRow(setPensionNomineeRows, i, "Photo", reader.result);
                                                    };
                                                    reader.readAsDataURL(file);
                                                  }
                                                }}
                                              />
                                            </label>
                                          )}
                                        </div>
                                      </td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NomineeName} onChange={(e) => changeRow(setPensionNomineeRows, i, "NomineeName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.Address} onChange={(e) => changeRow(setPensionNomineeRows, i, "Address", e.target.value)} /></td>
                                      <td>
                                        <select className="form-control form-control-sm" value={row.F_RelationshipMaster} onChange={(e) => changeRow(setPensionNomineeRows, i, "F_RelationshipMaster", e.target.value)}>
                                          <option value="">-- Select --</option>
                                          {filterActiveMasters(state.RelationshipMaster, row.F_RelationshipMaster).map((r) => <option key={r.Id} value={r.Id}>{r.Name}</option>)}
                                        </select>
                                      </td>
                                      <td><input type="date" className="form-control form-control-sm" value={row.BirthDate ? row.BirthDate.split("T")[0] : ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "BirthDate", e.target.value)} /></td>
                                      <td><input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setPensionNomineeRows, i, "Amount", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.ParentName} onChange={(e) => changeRow(setPensionNomineeRows, i, "ParentName", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AadharNo} onChange={(e) => changeRow(setPensionNomineeRows, i, "AadharNo", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.NameHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "NameHindi", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.AddressHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "AddressHindi", e.target.value)} /></td>
                                      <td><input type="text" className="form-control form-control-sm" value={row.RelHindi || ""} onChange={(e) => changeRow(setPensionNomineeRows, i, "RelHindi", e.target.value)} /></td>
                                      <ActionCell index={i} rows={pensionNomineeRows} onAdd={() => addRow(setPensionNomineeRows, emptyPensionNomineeRow)} onRemove={(idx) => removeRow(setPensionNomineeRows, idx)} />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Two Side-by-Side Tables: Min Wages & Extra PF Rate */}
                          <div className="row gx-2 mb-2">
                            <div className="col-md-6 mb-1">
                              <div className="card h-100 border shadow-none mb-0">
                                <div className="card-header py-1 px-2 bg-light">
                                  <span className="mb-0 fw-bold" style={{ fontSize: "11px" }}>Min. Wages Table</span>
                                </div>
                                <div className="card-body p-1 table-responsive">
                                  <table className="table table-bordered table-sm align-middle mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: 30 }} className="text-center">#</th>
                                        <th>Min. Wages</th>
                                        <th>Effective Date</th>
                                        <th style={{ width: 65 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {minWagesRows.map((row, i) => (
                                        <tr key={i}>
                                          <td className="text-center">{i + 1}</td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              className="form-control form-control-sm"
                                              value={row.MinWages}
                                              onChange={(e) => changeRow(setMinWagesRows, i, "MinWages", e.target.value)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                              onChange={(e) => changeRow(setMinWagesRows, i, "EffectiveDate", e.target.value)}
                                            />
                                          </td>
                                          <ActionCell index={i} rows={minWagesRows} onAdd={() => addRow(setMinWagesRows, emptyMinWagesRow)} onRemove={(idx) => removeRow(setMinWagesRows, idx)} />
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6 mb-1">
                              <div className="card h-100 border shadow-none mb-0">
                                <div className="card-header py-1 px-2 bg-light">
                                  <span className="mb-0 fw-bold" style={{ fontSize: "11px" }}>Extra PF Rate Table</span>
                                </div>
                                <div className="card-body p-1 table-responsive">
                                  <table className="table table-bordered table-sm align-middle mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: 30 }} className="text-center">#</th>
                                        <th>Extra PF Rate</th>
                                        <th style={{ width: 55 }} className="text-center">Manual</th>
                                        <th>Effective Date</th>
                                        <th style={{ width: 65 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {extraPFRateRows.map((row, i) => (
                                        <tr key={i}>
                                          <td className="text-center">{i + 1}</td>
                                          <td>
                                            <input
                                              type="number"
                                              step="0.01"
                                              className="form-control form-control-sm"
                                              value={row.ExtraPFRate}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "ExtraPFRate", e.target.value)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <input
                                              type="checkbox"
                                              checked={!!row.IsManual}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "IsManual", e.target.checked)}
                                            />
                                          </td>
                                          <td>
                                            <input
                                              type="date"
                                              className="form-control form-control-sm"
                                              value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                              onChange={(e) => changeRow(setExtraPFRateRows, i, "EffectiveDate", e.target.value)}
                                            />
                                          </td>
                                          <ActionCell index={i} rows={extraPFRateRows} onAdd={() => addRow(setExtraPFRateRows, emptyExtraPFRateRow)} onRemove={(idx) => removeRow(setExtraPFRateRows, idx)} />
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Opening Balance Fields */}
                          <div className="card border shadow-none mb-0" style={{ background: "#f8fbff", borderRadius: 6, padding: "6px 8px" }}>
                            <div className="card-body py-1 px-2">
                              <div className="row gx-2 gy-1 align-items-center">
                                <div className="col-12 col-md-4 emp-field-group">
                                  <label className="emp-field-label">Employee Part Op.Bal</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="EmployeePartOpBal" value={values.EmployeePartOpBal || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                                <div className="col-12 col-md-4 emp-field-group">
                                  <label className="emp-field-label">Employer Part Op.Bal</label>
                                  <input type="number" step="0.01" className="form-control form-control-sm" name="EmployerPartOpBal" value={values.EmployerPartOpBal || ""} onChange={handleChange} placeholder="0.00" />
                                </div>
                                <div className="col-12 col-md-4 emp-field-group">
                                  <label className="emp-field-label">Op. Bal Year</label>
                                  <input type="text" className="form-control form-control-sm" name="OpBalYear" value={values.OpBalYear || ""} onChange={handleChange} placeholder="e.g. 2024" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Leave Opening Balance (Carry Forward Leaves) */}
                          <div className="card border shadow-none bg-white mt-2 mb-0">
                            <div className="card-header py-1 px-2 bg-light d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center gap-2">
                                <i className="fas fa-calendar-alt text-primary" style={{ fontSize: "13px" }}></i>
                                <h6 className="fw-bold text-primary mb-0" style={{ fontSize: "12px" }}>
                                  Leave Opening Balance (Carry Forward Leaves – Previous Calendar Month / Cycle)
                                </h6>
                                <span className="badge bg-info-subtle text-info border border-info-subtle" style={{ fontSize: "10px" }}>
                                  Pichle Leave Calendar Month Ka Op. Balance
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-xs py-0 px-2"
                                  style={{ fontSize: "11px" }}
                                  onClick={() => {
                                    setLeaveOpeningRows(prev => [...prev, emptyLeaveOpeningRow()]);
                                  }}
                                  title="Add Extra Leave Row"
                                >
                                  <i className="fa fa-plus me-1" /> Add Leave Row
                                </button>
                              </div>
                            </div>
                            <div className="card-body py-1 px-2">
                              {leaveOpeningRows && leaveOpeningRows.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table table-bordered table-sm align-middle mb-1">
                                    <thead className="table-light text-nowrap" style={{ fontSize: "11px" }}>
                                      <tr>
                                        <th style={{ width: 35 }} className="text-center">#</th>
                                        <th style={{ minWidth: 160 }}>Leave Name</th>
                                        <th style={{ minWidth: 170 }}>Leave Calendar Cycle (From – To)</th>
                                        <th style={{ width: 130 }} className="text-center">Previous Cycle Year</th>
                                        <th style={{ width: 150 }} className="text-end">Opening Balance (Days)</th>
                                        <th style={{ width: 60 }} className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {leaveOpeningRows.map((row, i) => {
                                        const matchedMaster = (state.LeaveMaster || []).find(l => String(l.Id) === String(row.F_LeaveMaster));
                                        const displayLeaveName = row.LeaveName || matchedMaster?.Name || "Select Leave";
                                        const fromD = row.CalenderFromDay || matchedMaster?.CalenderFromDay || "01";
                                        const fromM = row.CalenderFromMonth || matchedMaster?.CalenderFromMonth || "01";
                                        const toD = row.CalenderToDay || matchedMaster?.CalenderToDay || "31";
                                        const toM = row.CalenderToMonth || matchedMaster?.CalenderToMonth || "12";

                                        return (
                                          <tr key={i}>
                                            <td className="text-center text-muted" style={{ fontSize: "11px" }}>{i + 1}</td>
                                            <td>
                                              {row.F_LeaveMaster && matchedMaster ? (
                                                <div className="d-flex align-items-center gap-2">
                                                  <span className="fw-bold text-dark">{displayLeaveName}</span>
                                                  <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: "9.5px" }}>
                                                    Carry Forward: YES
                                                  </span>
                                                </div>
                                              ) : (
                                                <select
                                                  className="form-select form-select-sm py-0"
                                                  style={{ fontSize: "11.5px", height: "26px" }}
                                                  value={row.F_LeaveMaster || ""}
                                                  onChange={(e) => {
                                                    const selectedId = e.target.value;
                                                    const sel = (state.LeaveMaster || []).find(l => String(l.Id) === String(selectedId));
                                                    setLeaveOpeningRows(prev => prev.map((r, idx) => {
                                                      if (idx !== i) return r;
                                                      return {
                                                        ...r,
                                                        F_LeaveMaster: selectedId,
                                                        LeaveName: sel?.Name || "",
                                                        CalenderFromMonth: sel?.CalenderFromMonth || "01",
                                                        CalenderFromDay: sel?.CalenderFromDay || "01",
                                                        CalenderToMonth: sel?.CalenderToMonth || "12",
                                                        CalenderToDay: sel?.CalenderToDay || "31",
                                                      };
                                                    }));
                                                  }}
                                                >
                                                  <option value="">-- Select Leave --</option>
                                                  {(state.LeaveMaster || []).map(l => (
                                                    <option key={l.Id} value={l.Id}>
                                                      {l.Name} {l.IsYearCarryforward ? "(Carry Forward)" : ""}
                                                    </option>
                                                  ))}
                                                </select>
                                              )}
                                            </td>
                                            <td>
                                              <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1" style={{ fontSize: "11px" }}>
                                                <i className="far fa-calendar-check me-1"></i>
                                                {formatCalendarMonthRange(fromD, fromM, toD, toM)}
                                              </span>
                                            </td>
                                            <td className="text-center">
                                              <input
                                                type="number"
                                                className="form-control form-control-sm text-center py-0"
                                                style={{ height: "26px", fontSize: "11.5px" }}
                                                value={row.OpBalYear || ""}
                                                placeholder="e.g. 2025"
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setLeaveOpeningRows(prev => prev.map((r, idx) => idx === i ? { ...r, OpBalYear: val } : r));
                                                }}
                                              />
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                step="0.01"
                                                className="form-control form-control-sm text-end fw-bold py-0"
                                                style={{ height: "26px", fontSize: "12px", color: "#1e40af" }}
                                                value={row.OpeningBalance !== undefined && row.OpeningBalance !== null ? row.OpeningBalance : ""}
                                                placeholder="0.00"
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setLeaveOpeningRows(prev => prev.map((r, idx) => idx === i ? { ...r, OpeningBalance: val } : r));
                                                }}
                                              />
                                            </td>
                                            <td className="text-center">
                                              <button
                                                type="button"
                                                className="btn btn-danger btn-xs py-0 px-1"
                                                title="Remove Row"
                                                onClick={() => {
                                                  setLeaveOpeningRows(prev => prev.filter((_, idx) => idx !== i));
                                                }}
                                              >
                                                <i className="fa fa-times" />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot className="table-light">
                                      <tr>
                                        <td colSpan="4" className="text-end fw-bold text-muted" style={{ fontSize: "11.5px" }}>
                                          Total Leave Opening Balance:
                                        </td>
                                        <td className="text-end fw-bold text-primary" style={{ fontSize: "12.5px" }}>
                                          {(leaveOpeningRows || []).reduce((acc, r) => acc + (parseFloat(r.OpeningBalance) || 0), 0).toFixed(2)} Days
                                        </td>
                                        <td></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              ) : (
                                <div className="alert alert-light border text-muted py-2 px-3 mb-1 d-flex align-items-center justify-content-between" style={{ fontSize: "11.5px" }}>
                                  <div>
                                    <i className="fas fa-info-circle text-info me-1"></i>
                                    Is firm me abhi koi <strong>Year Carry Forward</strong> leave configure nahi hai. Leave Master me leave ka <em>"Year Carry Forward: Yes"</em> aur <em>Leave Calendar Month</em> set karein, ya yahan se manually add karein.
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary py-0 px-2"
                                    style={{ fontSize: "11px" }}
                                    onClick={() => setLeaveOpeningRows([emptyLeaveOpeningRow()])}
                                  >
                                    <i className="fa fa-plus me-1"></i> Add Leave Opening
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      )}

                      {/* ════════════════════════════════════════════════════════════════════════
                          OTHER TABS: Dept History, Shift History, Join History, Allowances, Deductions
                          ════════════════════════════════════════════════════════════════════════ */}
                      
                      {/* Dept & Join History Tab (Combined) */}
                      {activeTab === "department" && (
                        <div className="tab-pane-content">
                          {/* 1. Department & Designation History */}
                          <div className="d-flex align-items-center justify-content-between mb-1 pb-1 border-bottom">
                            <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "12.5px" }}>
                              <i className="fas fa-building me-1"></i> Department &amp; Designation History
                            </h6>
                            <span className="badge bg-light text-muted border" style={{ fontSize: "10px" }}>
                              {deptRows.length} {deptRows.length === 1 ? "Row" : "Rows"}
                            </span>
                          </div>
                          <div className="table-responsive mb-3">
                            <table className="table table-bordered table-sm align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: 40 }} className="text-center">#</th>
                                  <th>Department</th>
                                  <th>Designation</th>
                                  <th>Effective Date</th>
                                  <th style={{ width: 85 }} className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deptRows.map((row, i) => {
                                  const deptOptions = state.DepartmentMaster
                                    ? state.DepartmentMaster
                                        .filter((d) => !isNotInUse(d) || String(d.Id) === String(row.F_DepartmentMaster))
                                        .map((d) => ({ value: String(d.Id), label: d.Name }))
                                    : [];
                                  const selectedDept = deptOptions.find((opt) => String(opt.value) === String(row.F_DepartmentMaster)) || null;

                                  const desigOptions = state.DesignationMaster
                                    ? state.DesignationMaster
                                        .filter((d) => !isNotInUse(d) || String(d.Id) === String(row.F_DesignationMaster))
                                        .map((d) => ({ value: String(d.Id), label: d.Name }))
                                    : [];
                                  const selectedDesig = desigOptions.find((opt) => String(opt.value) === String(row.F_DesignationMaster)) || null;

                                  const customSelectStyles = {
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base, state) => ({
                                      ...base,
                                      minHeight: "31px",
                                      height: "31px",
                                      fontSize: "12px",
                                      borderColor: state.isFocused ? "#2563eb" : "#cbd5e1",
                                      boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      height: "31px",
                                      padding: "0 6px",
                                    }),
                                    input: (base) => ({
                                      ...base,
                                      margin: 0,
                                      padding: 0,
                                      color: "#1e293b",
                                    }),
                                    indicatorsContainer: (base) => ({
                                      ...base,
                                      height: "31px",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      color: "#1e293b",
                                      fontWeight: 600,
                                      fontSize: "12px",
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                      backgroundColor: "#ffffff",
                                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                                    }),
                                    option: (base, state) => ({
                                      ...base,
                                      fontSize: "12px",
                                      padding: "6px 10px",
                                      cursor: "pointer",
                                      backgroundColor: state.isSelected
                                        ? "#1d4ed8 !important"
                                        : state.isFocused
                                        ? "#2563eb !important"
                                        : "#ffffff !important",
                                      color: state.isSelected || state.isFocused
                                        ? "#ffffff !important"
                                        : "#1e293b !important",
                                      fontWeight: state.isSelected ? 600 : 400,
                                    }),
                                  };

                                  return (
                                    <tr key={i}>
                                      <td className="align-middle text-center">{i + 1}</td>
                                      <td style={{ minWidth: "220px" }}>
                                        <Select
                                          options={deptOptions}
                                          value={selectedDept}
                                          onChange={(selectedOpt) =>
                                            changeDeptRow(i, "F_DepartmentMaster", selectedOpt ? selectedOpt.value : "", setFieldValue, values)
                                          }
                                          placeholder="-- Select Department --"
                                          isClearable
                                          isSearchable
                                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                          styles={customSelectStyles}
                                        />
                                      </td>
                                      <td style={{ minWidth: "220px" }}>
                                        <Select
                                          options={desigOptions}
                                          value={selectedDesig}
                                          onChange={(selectedOpt) =>
                                            changeDeptRow(i, "F_DesignationMaster", selectedOpt ? selectedOpt.value : "", setFieldValue, values)
                                          }
                                          placeholder="-- Select Designation --"
                                          isClearable
                                          isSearchable
                                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                          styles={customSelectStyles}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={row.EffectiveDate ? row.EffectiveDate.split("T")[0] : ""}
                                          onChange={(e) => changeDeptRow(i, "EffectiveDate", e.target.value, setFieldValue, values)}
                                        />
                                      </td>
                                      <ActionCell index={i} rows={deptRows} onAdd={() => addRow(setDeptRows, emptyDeptRow)} onRemove={(idx) => removeRow(setDeptRows, idx)} />
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* 2. Joining & Exit History */}
                          <div className="d-flex align-items-center justify-content-between mb-1 pb-1 border-bottom">
                            <h6 className="mb-0 fw-bold text-success" style={{ fontSize: "12.5px" }}>
                              <i className="fas fa-calendar-check me-1"></i> Joining &amp; Exit History
                            </h6>
                            <span className="badge bg-light text-muted border" style={{ fontSize: "10px" }}>
                              {joinRows.length} {joinRows.length === 1 ? "Row" : "Rows"}
                            </span>
                          </div>
                          <div className="table-responsive mb-2">
                            <table className="table table-bordered table-sm align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: 40 }} className="text-center">#</th>
                                  <th>Join Date</th>
                                  <th>Join Type</th>
                                  <th>Left Date</th>
                                  <th>Reason of Left</th>
                                  <th style={{ width: 85 }} className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {joinRows.map((row, i) => (
                                  <tr key={i}>
                                    <td className="align-middle text-center">{i + 1}</td>
                                    <td><input type="date" className="form-control form-control-sm" value={row.JoinDate ? row.JoinDate.split("T")[0] : ""} onChange={(e) => changeJoinRow(i, "JoinDate", e.target.value, setFieldValue, values)} /></td>
                                    <td>
                                      <select className="form-control form-control-sm" value={row.F_JoinTypeMaster} onChange={(e) => changeRow(setJoinRows, i, "F_JoinTypeMaster", e.target.value)}>
                                        <option value="">-- Select --</option>
                                        {filterActiveMasters(state.JoinTypeMaster, row.F_JoinTypeMaster).map((j) => <option key={j.Id} value={j.Id}>{j.Name}</option>)}
                                      </select>
                                    </td>
                                    <td><input type="date" className="form-control form-control-sm" value={row.LeftDate ? row.LeftDate.split("T")[0] : ""} onChange={(e) => changeRow(setJoinRows, i, "LeftDate", e.target.value)} /></td>
                                    <td>
                                      <select
                                        className="form-control form-control-sm"
                                        value={row.ReasonOfLeft || ""}
                                        onChange={(e) => changeRow(setJoinRows, i, "ReasonOfLeft", e.target.value)}
                                      >
                                        <option value="">-- Select Reason --</option>
                                        {row.ReasonOfLeft && !(state.ReasonOfLeftMaster || []).some((r) => r.Name === row.ReasonOfLeft) && (
                                          <option value={row.ReasonOfLeft}>{row.ReasonOfLeft}</option>
                                        )}
                                        {(state.ReasonOfLeftMaster || [])
                                          .filter((r) => !isNotInUse(r) || r.Name === row.ReasonOfLeft)
                                          .map((r) => (
                                            <option key={r.Id} value={r.Name}>
                                              {r.Name}
                                            </option>
                                          ))}
                                      </select>
                                    </td>
                                    <ActionCell index={i} rows={joinRows} onAdd={() => addRow(setJoinRows, () => ({ ...emptyJoinRow(getPrimaryEffectiveDate(), ""), F_JoinTypeMaster: "" }))} onRemove={(idx) => removeRow(setJoinRows, idx)} />
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Shift History Tab */}
                      {activeTab === "shift" && (
                        <div className="tab-pane-content table-responsive">
                          <table className="table table-bordered table-sm align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 40 }} className="text-center">#</th>
                                <th>Shift</th>
                                <th>Effective From</th>
                                <th style={{ width: 85 }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {shiftRows.map((row, i) => (
                                <tr key={i}>
                                  <td className="align-middle text-center">{i + 1}</td>
                                  <td>
                                    <select
                                      className="form-control form-control-sm"
                                      value={row.F_ShiftMaster}
                                      onChange={(e) => {
                                        const sId = e.target.value;
                                        changeRow(setShiftRows, i, "F_ShiftMaster", sId);
                                        if (sId && (!row.EffectiveFrom || row.EffectiveFrom === "")) {
                                          const eff = getPrimaryEffectiveDate();
                                          if (eff) changeRow(setShiftRows, i, "EffectiveFrom", eff);
                                        }
                                      }}
                                    >
                                      <option value="">-- Select --</option>
                                      {filterActiveMasters(availableShifts, row.F_ShiftMaster).map((s) => (
                                        <option key={s.Id} value={s.Id}>{s.Name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setShiftRows, i, "EffectiveFrom", e.target.value)} />
                                  </td>
                                  <ActionCell index={i} rows={shiftRows} onAdd={() => addRow(setShiftRows, emptyShiftRow)} onRemove={(idx) => removeRow(setShiftRows, idx)} />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Accident History Tab */}
                      {activeTab === "accidentHistory" && (
                        <div className="tab-pane-content">
                          <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded border">
                            <div className="d-flex align-items-center gap-3">
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="tabAccidentSwitch"
                                  checked={!!values.Accident}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFieldValue("Accident", checked);
                                    if (checked && (!accidentRows || accidentRows.length === 0)) {
                                      setAccidentRows([emptyAccidentRow()]);
                                    }
                                  }}
                                />
                                <label className="form-check-label fw-bold text-primary" htmlFor="tabAccidentSwitch">
                                  Accident Applicable
                                </label>
                              </div>
                              <span className="text-muted small">
                                {values.Accident ? "Manage multiple accident dates & history for this employee below." : "Enable accident coverage to record accident dates."}
                              </span>
                            </div>
                            {values.Accident && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => addRow(setAccidentRows, emptyAccidentRow)}
                              >
                                <i className="fa fa-plus me-1" /> Add Accident Date
                              </button>
                            )}
                          </div>

                          {values.Accident ? (
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm align-middle table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 45 }} className="text-center">#</th>
                                    <th style={{ width: 220 }}>Date of Accident *</th>
                                    <th>Remark / Reason / Incident Details</th>
                                    <th style={{ width: 85 }} className="text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(accidentRows && accidentRows.length > 0 ? accidentRows : [emptyAccidentRow()]).map((row, i) => (
                                    <tr key={i}>
                                      <td className="align-middle text-center fw-semibold">{i + 1}</td>
                                      <td>
                                        <input
                                          type="date"
                                          className="form-control form-control-sm"
                                          value={row.AccidentDate ? row.AccidentDate.split("T")[0] : ""}
                                          onChange={(e) => changeRow(setAccidentRows, i, "AccidentDate", e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder="Enter accident details / reason"
                                          value={row.Remark || ""}
                                          onChange={(e) => changeRow(setAccidentRows, i, "Remark", e.target.value)}
                                        />
                                      </td>
                                      <ActionCell
                                        index={i}
                                        rows={accidentRows && accidentRows.length > 0 ? accidentRows : [emptyAccidentRow()]}
                                        onAdd={() => addRow(setAccidentRows, emptyAccidentRow)}
                                        onRemove={(idx) => removeRow(setAccidentRows, idx)}
                                      />
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center p-4 text-muted bg-light rounded border border-dashed">
                              <i className="fa fa-info-circle fa-2x mb-2 text-warning" />
                              <h6>No Accident Records Enabled</h6>
                              <p className="small mb-2">To record accident dates and details for this employee, toggle "Accident Applicable" above or check "Accident" under ESI in the BASIC / SALARY tab.</p>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  setFieldValue("Accident", true);
                                  if (!accidentRows || accidentRows.length === 0) {
                                    setAccidentRows([emptyAccidentRow()]);
                                  }
                                }}
                              >
                                Enable Accident Records
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Allowances Tab */}
                      {activeTab === "allowances" && (() => {
                        const activeAllowanceIds = new Set(allowanceRows.map(r => String(r.F_AllowanceMaster)).filter(Boolean));
                        const sortedAllowanceMaster = [...(state.AllowanceMaster || [])]
                          .filter(a => !isNotInUse(a) || activeAllowanceIds.has(String(a.Id)))
                          .sort((a, b) => (a.Ordering || 0) - (b.Ordering || 0));

                        const groups = {};
                        allowanceRows.forEach((row, i) => {
                          const key = row.F_AllowanceMaster || `new_${i}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push({ row, index: i });
                        });
                        Object.values(groups).forEach(g => g.sort((a, b) => (a.row.EffectiveFrom || "").localeCompare(b.row.EffectiveFrom || "")));

                        const sortedEntries = Object.entries(groups).sort(([keyA], [keyB]) => {
                          const indexA = sortedAllowanceMaster.findIndex(a => String(a.Id) === String(keyA));
                          const indexB = sortedAllowanceMaster.findIndex(a => String(a.Id) === String(keyB));
                          if (indexA === -1 && indexB === -1) return 0;
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return (
                          <div className="tab-pane-content allowance-tab-pane">
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "12px" }}>
                                  <i className="fa fa-hand-holding-usd me-1" /> Employee Allowances
                                </h6>
                                <span className="badge bg-light text-muted border ms-2 py-1 px-2" style={{ fontSize: "10px", fontWeight: "normal" }}>
                                  Shift + Enter: Add Row &nbsp;|&nbsp; Enter: Next Field
                                </span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-success btn-xs"
                                onClick={handleAddNewAllowanceRow}
                              >
                                <i className="fa fa-plus me-1" /> Add Allowance
                              </button>
                            </div>
                            {sortedEntries.length === 0 && (
                              <div className="text-center p-3 text-muted bg-light rounded border border-dashed my-2">
                                <i className="fa fa-money-bill-wave fa-2x mb-2 text-primary" />
                                <h6 className="fw-semibold">No Allowances Configured</h6>
                                <p className="small mb-2">There are currently no active allowances assigned to this employee.</p>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-xs"
                                  onClick={handleAddNewAllowanceRow}
                                >
                                  <i className="fa fa-plus me-1" /> Add Allowance
                                </button>
                              </div>
                            )}
                            {sortedEntries.map(([key, entries]) => {
                              const total = entries.length;
                              const viewIdx = allowanceViewIndex[key] ?? (total - 1);
                              const { row, index: i } = entries[viewIdx] || entries[total - 1];
                              const allowanceName = sortedAllowanceMaster.find(a => String(a.Id) === String(row.F_AllowanceMaster))?.Name || `Allowance`;
                              const isExpanded = expandedAllowances[key] !== undefined ? !!expandedAllowances[key] : true;

                              return (
                                <div key={key} className="card mb-1">
                                  <div className="card-header py-1 px-2 d-flex justify-content-between align-items-center bg-light">
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="btn btn-sm py-0 px-1" style={{ fontSize: "10px", lineHeight: 1 }}
                                        onClick={() => setExpandedAllowances(prev => ({ ...prev, [key]: !isExpanded }))}>
                                        {isExpanded ? "▼" : "▶"}
                                      </button>
                                      <strong style={{ fontSize: "11.5px" }}>{allowanceName}</strong>
                                      {!isExpanded && (
                                        <small className="text-muted ms-2" style={{ fontSize: "10.5px" }}>
                                          Rate: {row.Amount || "-"} | {row.Calculation || "-"} | {row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : "-"}
                                          {row.IsPFApply ? " | PF" : ""}{row.IsESIApply ? " | ESI" : ""}{row.IsApplicable ? " | Active" : ""}
                                        </small>
                                      )}
                                      {total > 1 && (
                                        <span className="d-flex align-items-center gap-1 ms-2">
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === 0}
                                            onClick={() => setAllowanceViewIndex(prev => ({ ...prev, [key]: viewIdx - 1 }))}>
                                            ◀
                                          </button>
                                          <small className="text-muted">{viewIdx + 1}/{total}</small>
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === total - 1}
                                            onClick={() => setAllowanceViewIndex(prev => ({ ...prev, [key]: viewIdx + 1 }))}>
                                            ▶
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                    <ActionCell index={i} rows={allowanceRows} onAdd={handleAddNewAllowanceRow} onRemove={(idx) => removeRow(setAllowanceRows, idx)} />
                                  </div>
                                  {isExpanded && (
                                  <div className="card-body py-1 px-2">
                                    <div className="row gx-2 gy-1 mb-1 align-items-center">
                                      <div className="col-12 col-md-3 emp-field-group">
                                        <label className="emp-field-label">Allowance Name</label>
                                        <select
                                          className="form-control form-control-sm allowance-card-select"
                                          value={row.F_AllowanceMaster}
                                          onChange={(e) => {
                                            const newMasterId = e.target.value;
                                            changeRow(setAllowanceRows, i, "F_AllowanceMaster", newMasterId);
                                            if (newMasterId) {
                                              if (!row.EffectiveFrom || row.EffectiveFrom === "") {
                                                const eff = getPrimaryEffectiveDate();
                                                if (eff) changeRow(setAllowanceRows, i, "EffectiveFrom", eff);
                                              }
                                              setExpandedAllowances(prev => ({
                                                ...prev,
                                                [key]: true,
                                                [newMasterId]: true,
                                                [`new_${i}`]: true
                                              }));
                                            }
                                          }}
                                        >
                                          <option value="">-- Select --</option>
                                          {sortedAllowanceMaster.map((a) => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                                        </select>
                                      </div>
                                      <div className="col-12 col-md-2 emp-field-group">
                                        <label className="emp-field-label">Effective Date</label>
                                        <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setAllowanceRows, i, "EffectiveFrom", e.target.value)} />
                                      </div>
                                      <div className="col-12 col-md-2 emp-field-group">
                                        <label className="emp-field-label">Rate / Amount</label>
                                        <input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setAllowanceRows, i, "Amount", e.target.value)} />
                                      </div>
                                      <div className="col-12 col-md-5 emp-field-group">
                                        <label className="emp-field-label">Calculation</label>
                                        <div className="emp-control-box d-flex align-items-center justify-content-between px-2 gap-1" style={{ flexWrap: "nowrap" }}>
                                          {["MANUAL", "PERCENTAGE", "DAILY"].map((opt) => (
                                            <div key={opt} className="form-check form-check-inline mb-0">
                                              <input className="form-check-input" type="radio" name={`calc_${i}`} id={`calc_${i}_${opt}`} value={opt} checked={(row.Calculation || "").toUpperCase() === opt} onChange={(e) => changeAllowanceCalculation(i, e.target.value)} />
                                              <label className="form-check-label" htmlFor={`calc_${i}_${opt}`}>{opt}</label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-3 mt-1 pt-1 border-top">
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`onGross_${i}`} checked={!!row.IsOnGross} onChange={(e) => changeRow(setAllowanceRows, i, "IsOnGross", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`onGross_${i}`}>ON GROSS</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`pfApply_${i}`} checked={!!row.IsPFApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsPFApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`pfApply_${i}`}>PF APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`esiApply_${i}`} checked={!!row.IsESIApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsESIApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`esiApply_${i}`}>ESI APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`roundOff_${i}`} checked={!!row.IsRoundOffApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsRoundOffApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`roundOff_${i}`}>ROUNDED OFF</label></div>
                                      {((row.Calculation || "MANUAL").toUpperCase() === "MANUAL") && (
                                        <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`workDays_${i}`} checked={!!row.IsWorkingDaysApply} onChange={(e) => changeRow(setAllowanceRows, i, "IsWorkingDaysApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`workDays_${i}`}>WORKING DAYS APPLY</label></div>
                                      )}
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`applicable_${i}`} checked={!!row.IsApplicable} onChange={(e) => changeRow(setAllowanceRows, i, "IsApplicable", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`applicable_${i}`}>APPLICABLE</label></div>
                                    </div>
                                  </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Deductions Tab */}
                      {activeTab === "deductions" && (() => {
                        const currentFirmId = values.F_FirmMaster || firmId;
                        const activeDeductionIds = new Set(deductionRows.map(r => String(r.F_DeductionMaster)).filter(Boolean));
                        const sortedDeductionMaster = [...(state.DeductionMaster || [])]
                          .filter(d => {
                            const isFirmMatch = !currentFirmId || !d.F_FirmMaster || Number(d.F_FirmMaster) === Number(currentFirmId);
                            const isActive = !isNotInUse(d);
                            const isAlreadySelected = activeDeductionIds.has(String(d.Id));
                            return (isFirmMatch && isActive) || isAlreadySelected;
                          })
                          .sort((a, b) => (a.Ordering || 0) - (b.Ordering || 0));

                        const groups = {};
                        deductionRows.forEach((row, i) => {
                          const key = row.F_DeductionMaster || `new_${i}`;
                          if (!groups[key]) groups[key] = [];
                          groups[key].push({ row, index: i });
                        });
                        Object.values(groups).forEach(g => g.sort((a, b) => (a.row.EffectiveFrom || "").localeCompare(b.row.EffectiveFrom || "")));

                        const sortedEntries = Object.entries(groups).sort(([keyA], [keyB]) => {
                          const indexA = sortedDeductionMaster.findIndex(a => String(a.Id) === String(keyA));
                          const indexB = sortedDeductionMaster.findIndex(a => String(a.Id) === String(keyB));
                          if (indexA === -1 && indexB === -1) return 0;
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return (
                          <div className="tab-pane-content deduction-tab-pane">
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <h6 className="mb-0 fw-bold text-danger" style={{ fontSize: "12px" }}>
                                  <i className="fa fa-minus-circle me-1" /> Employee Deductions
                                </h6>
                                <span className="badge bg-light text-muted border ms-2 py-1 px-2" style={{ fontSize: "10px", fontWeight: "normal" }}>
                                  Shift + Enter: Add Row &nbsp;|&nbsp; Enter: Next Field
                                </span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-success btn-xs"
                                onClick={handleAddNewDeductionRow}
                              >
                                <i className="fa fa-plus me-1" /> Add Deduction
                              </button>
                            </div>
                            {sortedEntries.length === 0 && (
                              <div className="text-center p-3 text-muted bg-light rounded border border-dashed my-2">
                                <i className="fa fa-minus-circle fa-2x mb-2 text-danger" />
                                <h6 className="fw-semibold">No Deductions Configured</h6>
                                <p className="small mb-2">There are currently no active deductions assigned to this employee.</p>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-xs"
                                  onClick={handleAddNewDeductionRow}
                                >
                                  <i className="fa fa-plus me-1" /> Add Deduction
                                </button>
                              </div>
                            )}
                            {sortedEntries.map(([key, entries]) => {
                              const total = entries.length;
                              const viewIdx = deductionViewIndex[key] ?? (total - 1);
                              const { row, index: i } = entries[viewIdx] || entries[total - 1];
                              const deductionName = sortedDeductionMaster.find(a => String(a.Id) === String(row.F_DeductionMaster))?.Name
                                || (state.DeductionMaster || []).find(a => String(a.Id) === String(row.F_DeductionMaster))?.Name
                                || "New Deduction";
                              const isExpanded = expandedDeductions[key] !== undefined ? !!expandedDeductions[key] : true;

                              return (
                                <div key={key} className="card mb-1">
                                  <div className="card-header py-1 px-2 d-flex justify-content-between align-items-center bg-light">
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="btn btn-sm py-0 px-1" style={{ fontSize: "10px", lineHeight: 1 }}
                                        onClick={() => setExpandedDeductions(prev => ({ ...prev, [key]: !isExpanded }))}>
                                        {isExpanded ? "▼" : "▶"}
                                      </button>
                                      <strong style={{ fontSize: "11.5px" }}>{deductionName}</strong>
                                      {!isExpanded && (
                                        <small className="text-muted ms-2" style={{ fontSize: "10.5px" }}>
                                          Rate: {row.Amount || "-"} | {row.Calculation || "-"} | {row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : "-"}
                                          {row.IsPFApply ? " | PF" : ""}{row.IsESIApply ? " | ESI" : ""}{row.IsApplicable ? " | Active" : ""}
                                        </small>
                                      )}
                                      {total > 1 && (
                                        <span className="d-flex align-items-center gap-1 ms-2">
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === 0}
                                            onClick={() => setDeductionViewIndex(prev => ({ ...prev, [key]: viewIdx - 1 }))}>◀</button>
                                          <small className="text-muted">{viewIdx + 1}/{total}</small>
                                          <button type="button" className="btn btn-xs btn-outline-secondary py-0 px-1" disabled={viewIdx === total - 1}
                                            onClick={() => setDeductionViewIndex(prev => ({ ...prev, [key]: viewIdx + 1 }))}>▶</button>
                                        </span>
                                      )}
                                    </div>
                                    <ActionCell index={i} rows={deductionRows} onAdd={handleAddNewDeductionRow} onRemove={(idx) => removeRow(setDeductionRows, idx)} />
                                  </div>
                                  {isExpanded && (
                                  <div className="card-body py-1 px-2">
                                    <div className="row gx-2 gy-1 mb-1 align-items-center">
                                      <div className="col-12 col-md-3 emp-field-group">
                                        <label className="emp-field-label">Deduction Name</label>
                                        <select
                                          className="form-control form-control-sm deduction-card-select"
                                          value={row.F_DeductionMaster}
                                          onChange={(e) => {
                                            const newMasterId = e.target.value;
                                            changeRow(setDeductionRows, i, "F_DeductionMaster", newMasterId);
                                            if (newMasterId) {
                                              if (!row.EffectiveFrom || row.EffectiveFrom === "") {
                                                const eff = getPrimaryEffectiveDate();
                                                if (eff) changeRow(setDeductionRows, i, "EffectiveFrom", eff);
                                              }
                                              setExpandedDeductions(prev => ({
                                                ...prev,
                                                [key]: true,
                                                [newMasterId]: true,
                                                [`new_${i}`]: true
                                              }));
                                            }
                                          }}
                                        >
                                          <option value="">-- Select --</option>
                                          {sortedDeductionMaster.map((a) => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                                        </select>
                                      </div>
                                      <div className="col-12 col-md-2 emp-field-group">
                                        <label className="emp-field-label">Effective Date</label>
                                        <input type="date" className="form-control form-control-sm" value={row.EffectiveFrom ? row.EffectiveFrom.split("T")[0] : ""} onChange={(e) => changeRow(setDeductionRows, i, "EffectiveFrom", e.target.value)} />
                                      </div>
                                      <div className="col-12 col-md-2 emp-field-group">
                                        <label className="emp-field-label">Rate / Amount</label>
                                        <input type="number" step="0.01" className="form-control form-control-sm" value={row.Amount} onChange={(e) => changeRow(setDeductionRows, i, "Amount", e.target.value)} />
                                      </div>
                                      <div className="col-12 col-md-5 emp-field-group">
                                        <label className="emp-field-label">Calculation</label>
                                        <div className="emp-control-box d-flex align-items-center justify-content-between px-2 gap-1" style={{ flexWrap: "nowrap" }}>
                                          {["MANUAL", "PERCENTAGE", "DAILY"].map((opt) => (
                                            <div key={opt} className="form-check form-check-inline mb-0">
                                              <input className="form-check-input" type="radio" name={`dedcalc_${i}`} id={`dedcalc_${i}_${opt}`} value={opt} checked={(row.Calculation || "").toUpperCase() === opt} onChange={(e) => changeDeductionCalculation(i, e.target.value)} />
                                              <label className="form-check-label" htmlFor={`dedcalc_${i}_${opt}`}>{opt}</label>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-3 mt-1 pt-1 border-top">
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedonGross_${i}`} checked={!!row.IsOnGross} onChange={(e) => changeRow(setDeductionRows, i, "IsOnGross", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedonGross_${i}`}>ON GROSS</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedpfApply_${i}`} checked={!!row.IsPFApply} onChange={(e) => changeRow(setDeductionRows, i, "IsPFApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedpfApply_${i}`}>PF APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedesiApply_${i}`} checked={!!row.IsESIApply} onChange={(e) => changeRow(setDeductionRows, i, "IsESIApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedesiApply_${i}`}>ESI APPLY</label></div>
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedroundOff_${i}`} checked={!!row.IsRoundOffApply} onChange={(e) => changeRow(setDeductionRows, i, "IsRoundOffApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedroundOff_${i}`}>ROUNDED OFF</label></div>
                                      {((row.Calculation || "MANUAL").toUpperCase() === "MANUAL") && (
                                        <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedworkDays_${i}`} checked={!!row.IsWorkingDaysApply} onChange={(e) => changeRow(setDeductionRows, i, "IsWorkingDaysApply", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedworkDays_${i}`}>WORKING DAYS APPLY</label></div>
                                      )}
                                      <div className="form-check mb-0"><input className="form-check-input" type="checkbox" id={`dedapplicable_${i}`} checked={!!row.IsApplicable} onChange={(e) => changeRow(setDeductionRows, i, "IsApplicable", e.target.checked)} /><label className="form-check-label fw-semibold" htmlFor={`dedapplicable_${i}`}>APPLICABLE</label></div>
                                    </div>
                                  </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      </div>

                      {/* Pinned Bottom Footer Navigation */}
                      <div className="emp-footer-actions">
                        {activeTab !== tabOrder[tabOrder.length - 1].key && (
                          <button
                            type="button"
                            id="btnNextTab"
                            className="btn btn-info btn-sm"
                            onClick={handleNextTabClick}
                          >
                            Next <i className="fa fa-arrow-right ms-1" />
                          </button>
                        )}
                        <button
                          type="button"
                          id="btnSubmitForm"
                          className="btn btn-primary btn-sm"
                          disabled={isSubmitting}
                          onClick={async () => {
                            const formErrors = await validateForm();
                            if (formErrors && Object.keys(formErrors).length > 0) {
                              const touchedFields = {};
                              Object.keys(formErrors).forEach((key) => { touchedFields[key] = true; });
                              setTouched(touchedFields);

                              if (formErrors.UAN) {
                                toast.error(formErrors.UAN);
                                alert(formErrors.UAN);
                                setActiveTab("salary");
                                return;
                              }
                              if (formErrors.OldUAN) {
                                toast.error(formErrors.OldUAN);
                                alert(formErrors.OldUAN);
                                setActiveTab("salary");
                                return;
                              }
                              if (formErrors.F_FirmMaster || formErrors.Name || formErrors.MobileNo || formErrors.Email) {
                                const firstErr = formErrors.Name || formErrors.F_FirmMaster || formErrors.MobileNo || formErrors.Email;
                                toast.error(firstErr);
                                alert(firstErr);
                                setActiveTab("basicInfo");
                                return;
                              }
                              const firstKey = Object.keys(formErrors)[0];
                              toast.error(formErrors[firstKey]);
                              alert(formErrors[firstKey]);
                              return;
                            }
                            handleSubmit();
                          }}
                        >
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        {state.id > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete employee "${values.Name || state.id}"?`)) {
                                try {
                                  await Fn_DeleteData(dispatch, () => {}, Number(state.id), `${API_WEB_URLS.MASTER}/0/token/EmployeeMaster`);
                                  toast.success("Employee deleted successfully");
                                  navigate("/EmployeeMaster");
                                } catch (err) {
                                  console.error("Error deleting employee:", err);
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button type="button" className="btn btn-danger light btn-sm" onClick={() => navigate("/EmployeeMaster")}>
                          Cancel
                        </button>
                      </div>

                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_EmployeeMaster;
